import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import { useDropzone } from "react-dropzone";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import axios from "axios";
import moment from "moment";
import { PDFDocument } from "pdf-lib";
import apiUtils from "../../utils/apiUtils";
import { GlobalContext } from "../../contexts/GlobalContext";
import CustomerSearchSelect from "../../components/CustomerSearchSelect";
import CreateSupplierDialog from "../../components/manualInvoices/CreateSupplierDialog";
import {
  buildManualInvoiceFieldDefinitions,
  focusFirstEmptyInvoiceField,
} from "../../utils/focusFirstEmptyInvoiceField";

const emptyDraft = {
  manualInvoiceDraftId: null,
  customerNumber: "",
  invoiceNumber: "",
  invoiceDate: "",
  supplierId: "",
  countryCode: "",
  claimProductTypeId: "",
  currencyId: "",
  invoiceGrossAmount: "",
  invoiceVATAmountLocalCurrency: "",
  invoiceVATAmountEUR: "",
  notes: "",
  fileId: null,
  originalFileName: "",
  claimProductTypeCode: "",
  claimProductSubCode: "",
};

const parseNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatDateForInput = (value) => {
  if (!value) {
    return "";
  }

  return moment(value).format("YYYY-MM-DD");
};

const normalizeSubCode = (subCode) => {
  if (subCode === null || subCode === undefined || subCode === "") {
    return "98";
  }

  return String(subCode);
};

const formatProductCodeDisplay = (categoryCode, subCode) => {
  if (!categoryCode) {
    return "";
  }

  const normalizedSubCode = normalizeSubCode(subCode);
  if (normalizedSubCode === "98") {
    return String(categoryCode);
  }

  return `${categoryCode}.${normalizedSubCode}`;
};

const sortSubCodeOptions = (items) =>
  [...items].sort((a, b) => {
    const aIs98 = String(a.subCode) === "98";
    const bIs98 = String(b.subCode) === "98";
    if (aIs98 !== bIs98) {
      return aIs98 ? 1 : -1;
    }

    return String(a.subCode).localeCompare(String(b.subCode), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });

const formatAmountToTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) {
    return "";
  }

  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? String(value) : parsed.toFixed(2);
};

const getPdfViewerUrl = (url) => {
  if (!url) {
    return "";
  }

  return `${url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH&zoom=page-width`;
};

const buildSplitFileName = (originalFileName) => {
  const trimmedFileName = String(originalFileName || "").trim();
  if (!trimmedFileName) {
    return `manual-invoice-split-${Date.now()}.pdf`;
  }

  const extensionIndex = trimmedFileName.lastIndexOf(".");
  if (extensionIndex <= 0) {
    return `${trimmedFileName}-split.pdf`;
  }

  const baseName = trimmedFileName.slice(0, extensionIndex);
  const extension = trimmedFileName.slice(extensionIndex);
  return `${baseName}-split${extension || ".pdf"}`;
};

const buildRemainingFileName = (originalFileName) => {
  const trimmedFileName = String(originalFileName || "").trim();
  if (!trimmedFileName) {
    return `manual-invoice-remainder-${Date.now()}.pdf`;
  }

  const extensionIndex = trimmedFileName.lastIndexOf(".");
  if (extensionIndex <= 0) {
    return `${trimmedFileName}-remainder.pdf`;
  }

  const baseName = trimmedFileName.slice(0, extensionIndex);
  const extension = trimmedFileName.slice(extensionIndex);
  return `${baseName}-remainder${extension || ".pdf"}`;
};

const getUserDisplayName = (displayName, userId) => {
  return displayName || userId || "";
};

const getHistoricUploadDateMoment = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = moment.isDate(value) ? moment(value) : moment(value, "DD-MM-YYYY", true);
  return parsedDate.isValid() ? parsedDate.startOf("day") : null;
};

const formatHistoricUploadDateForApi = (value) => {
  const parsedDate = getHistoricUploadDateMoment(value);
  return parsedDate ? parsedDate.format("YYYY-MM-DD") : "";
};

const isValidHistoricUploadDate = (value) => Boolean(formatHistoricUploadDateForApi(value));

const isCompleteHistoricUploadDateInput = (inputValue) => {
  if (inputValue === undefined || inputValue === null) {
    return true;
  }

  return /^\d{2}-\d{2}-\d{4}$/.test(inputValue);
};

const isInvalidCompleteHistoricUploadDateInput = (inputValue) => {
  if (!inputValue || !isCompleteHistoricUploadDateInput(inputValue)) {
    return false;
  }

  return !moment(inputValue, "DD-MM-YYYY", true).isValid();
};

const showHistoricUploadsButton = true;
const showHistoricUploadRows = true;
const emptyHistoricUploadFilters = {
  createdDateFrom: null,
  createdDateTo: null,
  lastUpdatedFrom: null,
  lastUpdatedTo: null,
};
const clipboardSlotNumbers = [1, 2, 3, 4, 5];
const minClipboardOverlayLeft = 260;
const createEmptyClipboardSlots = () =>
  clipboardSlotNumbers.map((slotNumber) => ({
    slotNumber,
    customerManualInvoiceClipboardSlotId: null,
    countryCode: null,
    supplierId: null,
    categoryCode: "",
    productSubCode: "",
    lastUpdated: null,
    lastUpdatedBy: "",
    hasValues: false,
  }));

const ManualInvoices = () => {
  const { globalData } = useContext(GlobalContext);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [publishRows, setPublishRows] = useState([]);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectedCustomerNumber, setSelectedCustomerNumber] = useState("");
  const [selectedUploadCustomerNumber, setSelectedUploadCustomerNumber] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [rightPaneSize, setRightPaneSize] = useState(35);
  const [detailDraft, setDetailDraft] = useState(emptyDraft);
  const [customerNumberError, setCustomerNumberError] = useState("");
  const [invoiceNumberInfo, setInvoiceNumberInfo] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [draftIdsToDelete, setDraftIdsToDelete] = useState([]);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [isBatchDialogOpen, setIsBatchDialogOpen] = useState(false);
  const [isHistoricUploadsDialogOpen, setIsHistoricUploadsDialogOpen] = useState(false);
  const [historicUploads, setHistoricUploads] = useState([]);
  const [historicUploadFilters, setHistoricUploadFilters] = useState(emptyHistoricUploadFilters);
  const [expandedHistoricUploadCustomers, setExpandedHistoricUploadCustomers] = useState({});
  const [loadingHistoricUploads, setLoadingHistoricUploads] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
  const [draftFileHistory, setDraftFileHistory] = useState([]);
  const [loadingDraftFileHistory, setLoadingDraftFileHistory] = useState(false);
  const [isOldFilesDialogOpen, setIsOldFilesDialogOpen] = useState(false);
  const [restoringOldFileId, setRestoringOldFileId] = useState(null);
  const [activeOldFilePreviewId, setActiveOldFilePreviewId] = useState(null);
  const [oldFilePreviewUrl, setOldFilePreviewUrl] = useState("");
  const [loadingOldFilePreview, setLoadingOldFilePreview] = useState(false);
  const [matchedSupplierNotes, setMatchedSupplierNotes] = useState([]);
  const [customerClipboardSlots, setCustomerClipboardSlots] = useState(createEmptyClipboardSlots);
  const [loadingClipboardSlots, setLoadingClipboardSlots] = useState(false);
  const [noteOverlayPosition, setNoteOverlayPosition] = useState({ top: 16, left: null });
  const [isNoteOverlayDragging, setIsNoteOverlayDragging] = useState(false);
  const [clipboardOverlayPosition, setClipboardOverlayPosition] = useState({ top: 12, left: minClipboardOverlayLeft });
  const [isClipboardOverlayDragging, setIsClipboardOverlayDragging] = useState(false);
  const [isClipboardOverlayMinimized, setIsClipboardOverlayMinimized] = useState(false);
  const [isCreateSupplierDialogOpen, setIsCreateSupplierDialogOpen] = useState(false);
  const [createdSuppliers, setCreatedSuppliers] = useState([]);
  const [duplicatingDraft, setDuplicatingDraft] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [splittingDraft, setSplittingDraft] = useState(false);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);
  const [splitPageCount, setSplitPageCount] = useState(0);
  const [selectedSplitPages, setSelectedSplitPages] = useState([]);
  const [activeSplitPreviewPage, setActiveSplitPreviewPage] = useState(1);
  const [splitPagePreviews, setSplitPagePreviews] = useState({});
  const [loadingSplitPagePreviews, setLoadingSplitPagePreviews] = useState(false);
  const [removeSelectedPagesFromCurrentDraft, setRemoveSelectedPagesFromCurrentDraft] = useState(true);
  const saveTimeoutRef = useRef(null);
  const noteOverlayDragOffsetRef = useRef({ x: 0, y: 0 });
  const clipboardOverlayDragOffsetRef = useRef({ x: 0, y: 0 });
  const invoiceDateInputRef = useRef(null);
  const invoiceDetailPanelRef = useRef(null);
  const shouldFocusFirstEmptyFieldRef = useRef(false);
  const splitPreviewRunRef = useRef(0);
  const responsiveFieldSx = { width: "100%", minWidth: 150, maxWidth: 230 };

  const supplierData = apiUtils.useApiData("/api/v1/suppliers");
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const currencyData = apiUtils.useApiData("/api/v1/currencies");
  const productData = apiUtils.useApiData("/api/v1/productTypes");
  const entityData = apiUtils.useApiData("/api/v1/entities");
  const customerInvoicesData = apiUtils.useApiData("/api/v1/customerInvoices");

  const mergedSupplierData = useMemo(() => {
    if (!createdSuppliers.length) {
      return supplierData;
    }

    const suppliersById = new Map();
    [...supplierData, ...createdSuppliers].forEach((supplier) => {
      if (!supplier?.supplierId) {
        return;
      }

      suppliersById.set(supplier.supplierId, supplier);
    });

    return Array.from(suppliersById.values());
  }, [createdSuppliers, supplierData]);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/v1/manualInvoiceDrafts");
      setDrafts(response.data ?? []);
    } catch (error) {
      setSnackbar({ open: true, severity: "error", message: "Unable to load manual drafts." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const supplierOptions = useMemo(() => {
    const filteredSuppliers = detailDraft.countryCode
      ? mergedSupplierData.filter((supplier) => supplier.countryCode === detailDraft.countryCode)
      : [];

    return [...filteredSuppliers].sort((a, b) => (a.supplierName || "").localeCompare(b.supplierName || ""));
  }, [detailDraft.countryCode, mergedSupplierData]);

  const handleSelectDraft = useCallback(
    (draft) => {
      shouldFocusFirstEmptyFieldRef.current = true;
      setSelectedDraftId(draft.manualInvoiceDraftId);
      setDetailDraft({
        manualInvoiceDraftId: draft.manualInvoiceDraftId,
        customerNumber: draft.customerNumber || "",
        invoiceNumber: draft.invoiceNumber || "",
        invoiceDate: formatDateForInput(draft.invoiceDate),
        supplierId: draft.supplierId || "",
        countryCode: draft.countryCode || "",
        claimProductTypeId: draft.claimProductTypeId || "",
        currencyId: draft.currencyId || "",
        invoiceGrossAmount: formatAmountToTwoDecimals(draft.invoiceGrossAmount ?? ""),
        invoiceVATAmountLocalCurrency: formatAmountToTwoDecimals(draft.invoiceVATAmountLocalCurrency ?? ""),
        invoiceVATAmountEUR: draft.invoiceVATAmountEUR ?? "",
        notes: draft.notes || "",
        fileId: draft.fileId || null,
        originalFileName: draft.originalFileName || "",
        claimProductTypeCode: "",
        claimProductSubCode: "",
      });

      if (draft.countryCode && draft.claimProductTypeId) {
        const matchedProduct = productData.find((item) => item.productTypeId === draft.claimProductTypeId);
        if (matchedProduct) {
          setDetailDraft((prev) => ({
            ...prev,
            claimProductTypeCode: matchedProduct.categoryCode || "",
            claimProductSubCode: normalizeSubCode(matchedProduct.subCode),
          }));
        }
      }
      setRightPaneSize((prevSize) => (prevSize === 0 ? 35 : prevSize));
    },
    [productData, setRightPaneSize]
  );

  useEffect(() => {
    if (!shouldFocusFirstEmptyFieldRef.current || !selectedDraftId) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      shouldFocusFirstEmptyFieldRef.current = false;
      focusFirstEmptyInvoiceField(
        invoiceDetailPanelRef.current,
        buildManualInvoiceFieldDefinitions(detailDraft, entityData)
      );
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [selectedDraftId, detailDraft, entityData]);

  useEffect(() => {
    if (!selectedDraftId) {
      return;
    }

    const current = drafts.find((item) => item.manualInvoiceDraftId === selectedDraftId);
    if (!current) {
      setSelectedDraftId(null);
      setDetailDraft(emptyDraft);
      return;
    }

    if (current.manualInvoiceDraftId !== detailDraft.manualInvoiceDraftId) {
      handleSelectDraft(current);
    }
  }, [drafts, selectedDraftId, detailDraft.manualInvoiceDraftId, handleSelectDraft]);

  useEffect(() => {
    if (!detailDraft.fileId) {
      setPdfUrl("");
      setPdfBlob(null);
      return;
    }

    let localUrl = "";
    const loadPdf = async () => {
      try {
        const response = await axios.get(`/api/v1/azureblobstorage/${detailDraft.fileId}`, { responseType: "blob" });
        setPdfBlob(response.data);
        localUrl = URL.createObjectURL(response.data);
        setPdfUrl(localUrl);
      } catch (error) {
        setPdfUrl("");
        setPdfBlob(null);
      }
    };

    loadPdf();

    return () => {
      if (localUrl) {
        URL.revokeObjectURL(localUrl);
      }
    };
  }, [detailDraft.fileId]);

  const loadDraftFileHistory = useCallback(async (draftId) => {
    if (!draftId) {
      setDraftFileHistory([]);
      setLoadingDraftFileHistory(false);
      return;
    }

    try {
      setLoadingDraftFileHistory(true);
      const response = await axios.get(`/api/v1/manualInvoiceDrafts/${draftId}/fileHistory`);
      setDraftFileHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setDraftFileHistory([]);
    } finally {
      setLoadingDraftFileHistory(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedDraftId) {
      setDraftFileHistory([]);
      setLoadingDraftFileHistory(false);
      return;
    }

    let isCancelled = false;
    const loadHistory = async () => {
      await loadDraftFileHistory(selectedDraftId);
      if (isCancelled) {
        return;
      }
    };

    void loadHistory();
    return () => {
      isCancelled = true;
    };
  }, [loadDraftFileHistory, selectedDraftId]);

  const oldFilesList = useMemo(() => {
    const filesById = new Map();
    draftFileHistory.forEach((historyItem) => {
      if (!historyItem?.previousFileId) {
        return;
      }

      const existing = filesById.get(historyItem.previousFileId);
      if (!existing || new Date(historyItem.performedDate).getTime() > new Date(existing.performedDate).getTime()) {
        filesById.set(historyItem.previousFileId, historyItem);
      }
    });

    return Array.from(filesById.values()).sort(
      (a, b) => new Date(b.performedDate).getTime() - new Date(a.performedDate).getTime()
    );
  }, [draftFileHistory]);

  useEffect(() => {
    if (!isOldFilesDialogOpen || oldFilesList.length === 0) {
      setActiveOldFilePreviewId(null);
      return;
    }

    if (!activeOldFilePreviewId || !oldFilesList.some((item) => item.previousFileId === activeOldFilePreviewId)) {
      setActiveOldFilePreviewId(oldFilesList[0].previousFileId);
    }
  }, [activeOldFilePreviewId, isOldFilesDialogOpen, oldFilesList]);

  useEffect(() => {
    if (!isOldFilesDialogOpen || !activeOldFilePreviewId) {
      setOldFilePreviewUrl("");
      setLoadingOldFilePreview(false);
      return;
    }

    let localUrl = "";
    let isCancelled = false;
    const loadPreview = async () => {
      try {
        setLoadingOldFilePreview(true);
        const response = await axios.get(`/api/v1/azureblobstorage/${activeOldFilePreviewId}`, { responseType: "blob" });
        if (isCancelled) {
          return;
        }

        localUrl = URL.createObjectURL(response.data);
        setOldFilePreviewUrl(localUrl);
      } catch (error) {
        if (!isCancelled) {
          setOldFilePreviewUrl("");
        }
      } finally {
        if (!isCancelled) {
          setLoadingOldFilePreview(false);
        }
      }
    };

    void loadPreview();
    return () => {
      isCancelled = true;
      if (localUrl) {
        URL.revokeObjectURL(localUrl);
      }
    };
  }, [activeOldFilePreviewId, isOldFilesDialogOpen]);

  const scheduleAutosave = useCallback((nextDraft) => {
    if (!nextDraft.manualInvoiceDraftId) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`/api/v1/manualInvoiceDrafts/${nextDraft.manualInvoiceDraftId}`, {
          customerNumber: nextDraft.customerNumber || null,
          invoiceNumber: nextDraft.invoiceNumber || null,
          invoiceDate: nextDraft.invoiceDate || null,
          supplierId: nextDraft.supplierId || null,
          countryCode: nextDraft.countryCode || null,
          claimProductTypeId: nextDraft.claimProductTypeId || null,
          currencyId: nextDraft.currencyId || null,
          invoiceGrossAmount: parseNumberOrNull(nextDraft.invoiceGrossAmount),
          invoiceVATAmountLocalCurrency: parseNumberOrNull(nextDraft.invoiceVATAmountLocalCurrency),
          invoiceVATAmountEUR: parseNumberOrNull(nextDraft.invoiceVATAmountEUR),
          dataEntryOperator: "System",
          notes: nextDraft.notes || null,
        });

        setDrafts((prev) =>
          prev.map((item) =>
            item.manualInvoiceDraftId === nextDraft.manualInvoiceDraftId
              ? {
                  ...item,
                  ...nextDraft,
                  invoiceDate: nextDraft.invoiceDate || null,
                  invoiceGrossAmount: parseNumberOrNull(nextDraft.invoiceGrossAmount),
                  invoiceVATAmountLocalCurrency: parseNumberOrNull(nextDraft.invoiceVATAmountLocalCurrency),
                  invoiceVATAmountEUR: parseNumberOrNull(nextDraft.invoiceVATAmountEUR),
                  lastUpdated: new Date().toISOString(),
                }
              : item
          )
        );
      } catch (error) {
        setSnackbar({ open: true, severity: "error", message: "Autosave failed for draft." });
      }
    }, 450);
  }, []);

  const handleDetailChange = useCallback(
    (field, value) => {
      const nextDraft = { ...detailDraft, [field]: value };
      setDetailDraft(nextDraft);
      scheduleAutosave(nextDraft);
    },
    [detailDraft, scheduleAutosave]
  );

  const handleCloseCreateSupplierDialog = useCallback(() => {
    setIsCreateSupplierDialogOpen(false);
  }, []);

  const handleSupplierCreated = useCallback(
    (newSupplier) => {
      setCreatedSuppliers((prevSuppliers) => {
        const existingIndex = prevSuppliers.findIndex((supplier) => supplier.supplierId === newSupplier.supplierId);
        if (existingIndex === -1) {
          return [...prevSuppliers, newSupplier];
        }

        return prevSuppliers.map((supplier, index) => (index === existingIndex ? newSupplier : supplier));
      });

      setIsCreateSupplierDialogOpen(false);

      if (!newSupplier?.supplierId) {
        return;
      }

      const nextCountryCode = newSupplier.countryCode || detailDraft.countryCode;
      const countryChanged = Boolean(nextCountryCode && nextCountryCode !== detailDraft.countryCode);
      const matchingCountry = countryData.find((item) => item.countryId === nextCountryCode);
      const nextDraft = {
        ...detailDraft,
        countryCode: nextCountryCode || detailDraft.countryCode,
        supplierId: newSupplier.supplierId,
        claimProductTypeCode: countryChanged ? "" : detailDraft.claimProductTypeCode,
        claimProductSubCode: countryChanged ? "" : detailDraft.claimProductSubCode,
        claimProductTypeId: countryChanged ? "" : detailDraft.claimProductTypeId,
        currencyId: countryChanged ? matchingCountry?.currency || "" : detailDraft.currencyId,
      };

      setDetailDraft(nextDraft);
      scheduleAutosave(nextDraft);
    },
    [countryData, detailDraft, scheduleAutosave]
  );

  useEffect(() => {
    if (!selectedDraftId) {
      setCustomerNumberError("");
      return;
    }

    const trimmedCustomerNumber = String(detailDraft.customerNumber || "").trim();
    if (!trimmedCustomerNumber) {
      setCustomerNumberError("");
      return;
    }

    const customerExists = entityData.some((entity) => entity.entityNumber === trimmedCustomerNumber);
    setCustomerNumberError(customerExists ? "" : "Customer number does not exist.");
  }, [detailDraft.customerNumber, entityData, selectedDraftId]);

  const isDuplicateInvoiceForCustomer = useCallback(
    (draft) => {
      const trimmedCustomerNumber = String(draft.customerNumber || "").trim();
      const trimmedInvoiceNumber = String(draft.invoiceNumber || "").trim().toLowerCase();

      if (!trimmedCustomerNumber || !trimmedInvoiceNumber) {
        return false;
      }

      const hasDuplicateAmongDrafts = drafts.some(
        (otherDraft) =>
          otherDraft.manualInvoiceDraftId !== draft.manualInvoiceDraftId &&
          String(otherDraft.customerNumber || "").trim() === trimmedCustomerNumber &&
          String(otherDraft.invoiceNumber || "").trim().toLowerCase() === trimmedInvoiceNumber
      );

      if (hasDuplicateAmongDrafts) {
        return true;
      }

      const customer = entityData.find(
        (entity) => String(entity.entityNumber || "").trim() === trimmedCustomerNumber
      );
      if (!customer?.entityId || !customerInvoicesData?.length) {
        return false;
      }

      return customerInvoicesData.some(
        (invoice) =>
          Number(invoice.entityId) === Number(customer.entityId) &&
          String(invoice.invoiceNumber || "").trim().toLowerCase() === trimmedInvoiceNumber
      );
    },
    [customerInvoicesData, drafts, entityData]
  );

  const checkInvoiceNumberDuplicate = useCallback(async () => {
    setInvoiceNumberInfo(
      isDuplicateInvoiceForCustomer(detailDraft) ? "Info: invoice number already exists for this customer." : ""
    );
  }, [detailDraft, isDuplicateInvoiceForCustomer]);

  const handleInvoiceNumberBlur = useCallback(async () => {
    await checkInvoiceNumberDuplicate();
  }, [checkInvoiceNumberDuplicate]);

  const selectedDetailCustomer = useMemo(() => {
    const trimmedCustomerNumber = String(detailDraft.customerNumber || "").trim();
    if (!trimmedCustomerNumber) {
      return null;
    }

    return (
      entityData.find((entity) => String(entity.entityNumber || "").trim() === trimmedCustomerNumber) || null
    );
  }, [detailDraft.customerNumber, entityData]);
  const selectedDetailEntityId = selectedDetailCustomer?.entityId || null;

  const normalizeClipboardSlots = useCallback((rawSlots) => {
    const slotsByNumber = new Map();
    (rawSlots || []).forEach((slot) => {
      if (!slot || !clipboardSlotNumbers.includes(Number(slot.slotNumber))) {
        return;
      }

      const normalizedSlotNumber = Number(slot.slotNumber);
      const normalizedCategoryCode = String(slot.categoryCode || "").trim();
      const normalizedProductSubCode = String(slot.productSubCode || "").trim();
      slotsByNumber.set(normalizedSlotNumber, {
        slotNumber: normalizedSlotNumber,
        customerManualInvoiceClipboardSlotId: slot.customerManualInvoiceClipboardSlotId || null,
        countryCode: slot.countryCode || null,
        supplierId: slot.supplierId || null,
        categoryCode: normalizedCategoryCode,
        productSubCode: normalizedProductSubCode,
        lastUpdated: slot.lastUpdated || null,
        lastUpdatedBy: slot.lastUpdatedBy || "",
      });
    });

    return clipboardSlotNumbers.map((slotNumber) => {
      const existingSlot = slotsByNumber.get(slotNumber);
      if (!existingSlot) {
        return {
          slotNumber,
          customerManualInvoiceClipboardSlotId: null,
          countryCode: null,
          supplierId: null,
          categoryCode: "",
          productSubCode: "",
          lastUpdated: null,
          lastUpdatedBy: "",
          hasValues: false,
        };
      }

      const hasValues = Boolean(
        existingSlot.countryCode || existingSlot.supplierId || existingSlot.categoryCode || existingSlot.productSubCode
      );

      return {
        ...existingSlot,
        hasValues,
      };
    });
  }, []);

  const loadCustomerClipboardSlots = useCallback(
    async (entityId) => {
      if (!entityId) {
        setCustomerClipboardSlots(createEmptyClipboardSlots());
        return;
      }

      setLoadingClipboardSlots(true);
      try {
        const response = await axios.get(`/api/v1/entities/${entityId}/manualInvoiceClipboardSlots`);
        setCustomerClipboardSlots(normalizeClipboardSlots(response.data));
      } catch (error) {
        setCustomerClipboardSlots(createEmptyClipboardSlots());
        setSnackbar({ open: true, severity: "error", message: "Unable to load customer clipboards." });
      } finally {
        setLoadingClipboardSlots(false);
      }
    },
    [normalizeClipboardSlots]
  );

  useEffect(() => {
    if (!selectedDraftId || !selectedDetailEntityId) {
      setCustomerClipboardSlots(createEmptyClipboardSlots());
      setLoadingClipboardSlots(false);
      return;
    }

    void loadCustomerClipboardSlots(selectedDetailEntityId);
  }, [loadCustomerClipboardSlots, selectedDetailEntityId, selectedDraftId]);

  const isDuplicateActionDisabled = useMemo(() => {
    if (!selectedDraftId) {
      return true;
    }

    return duplicatingDraft;
  }, [duplicatingDraft, selectedDraftId]);

  const isSplitActionDisabled = useMemo(() => {
    if (!selectedDraftId) {
      return true;
    }

    return splittingDraft;
  }, [selectedDraftId, splittingDraft]);

  const duplicateInvoiceNumber = useMemo(() => {
    const trimmedInvoiceNumber = String(detailDraft.invoiceNumber || "").trim();
    return trimmedInvoiceNumber ? `Dupl-${trimmedInvoiceNumber}` : "Duplicate";
  }, [detailDraft.invoiceNumber]);

  const handleOpenDuplicateDialog = useCallback(() => {
    if (!selectedDraftId || duplicatingDraft) {
      return;
    }

    const trimmedCustomerNumber = String(detailDraft.customerNumber || "").trim();
    if (!trimmedCustomerNumber) {
      setSnackbar({ open: true, severity: "warning", message: "Customer number is required to duplicate." });
      return;
    }

    if (!selectedDetailCustomer) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Enter a valid customer number before duplicating.",
      });
      return;
    }

    if (!detailDraft.fileId) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "This draft has no document and cannot be duplicated.",
      });
      return;
    }

    setIsDuplicateDialogOpen(true);
  }, [detailDraft.customerNumber, detailDraft.fileId, duplicatingDraft, selectedDetailCustomer, selectedDraftId]);

  const handleConfirmDuplicateDraft = useCallback(async () => {
    const trimmedCustomerNumber = String(detailDraft.customerNumber || "").trim();
    if (!trimmedCustomerNumber || !selectedDetailCustomer || !detailDraft.fileId || duplicatingDraft) {
      setIsDuplicateDialogOpen(false);
      return;
    }

    try {
      setDuplicatingDraft(true);
      setIsDuplicateDialogOpen(false);

      const sourceFileResponse = await axios.get(`/api/v1/azureblobstorage/${detailDraft.fileId}`, { responseType: "blob" });
      const sourceBlob = sourceFileResponse.data;
      const sourceFileName = String(detailDraft.originalFileName || "").trim() || "manual-invoice.pdf";
      const sourceMimeType = sourceBlob?.type || "application/pdf";

      const duplicatedFileInfoResponse = await axios.post("/api/v1/file", {
        documentType: 1,
        creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        createdBy: globalData?.userId || 1,
        fileName: sourceFileName,
        fileSize: sourceBlob.size,
        mimeType: sourceMimeType,
      });
      const duplicatedFileId = duplicatedFileInfoResponse?.data?.fileId;
      if (!duplicatedFileId) {
        throw new Error("Failed to create duplicate file metadata.");
      }

      const duplicatedFormData = new FormData();
      duplicatedFormData.append("file", sourceBlob, duplicatedFileId);
      await axios.post("/api/v1/azureblobstorage", duplicatedFormData);

      const response = await axios.post("/api/v1/manualInvoiceDrafts", {
        customerNumber: trimmedCustomerNumber,
        invoiceNumber: duplicateInvoiceNumber,
        fileId: duplicatedFileId,
        originalFileName: sourceFileName,
      });

      const duplicatedDraft = response.data;
      if (!duplicatedDraft?.manualInvoiceDraftId) {
        throw new Error("Missing duplicated draft identifier.");
      }

      await axios.put(`/api/v1/manualInvoiceDrafts/${duplicatedDraft.manualInvoiceDraftId}`, {
        customerNumber: trimmedCustomerNumber,
        invoiceNumber: duplicateInvoiceNumber,
        invoiceDate: duplicatedDraft.invoiceDate || null,
        supplierId: duplicatedDraft.supplierId || null,
        countryCode: duplicatedDraft.countryCode || null,
        claimProductTypeId: duplicatedDraft.claimProductTypeId || null,
        currencyId: duplicatedDraft.currencyId || null,
        invoiceGrossAmount: parseNumberOrNull(duplicatedDraft.invoiceGrossAmount),
        invoiceVATAmountLocalCurrency: parseNumberOrNull(duplicatedDraft.invoiceVATAmountLocalCurrency),
        invoiceVATAmountEUR: parseNumberOrNull(duplicatedDraft.invoiceVATAmountEUR),
        dataEntryOperator: "System",
        notes: duplicatedDraft.notes || null,
      });

      const duplicatedDraftWithInvoiceNumber = {
        ...duplicatedDraft,
        customerNumber: trimmedCustomerNumber,
        invoiceNumber: duplicateInvoiceNumber,
      };

      setDrafts((prev) => [duplicatedDraftWithInvoiceNumber, ...prev]);
      handleSelectDraft(duplicatedDraftWithInvoiceNumber);
      setSnackbar({ open: true, severity: "success", message: "Invoice duplicated." });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Unable to duplicate invoice and document.",
      });
    } finally {
      setDuplicatingDraft(false);
    }
  }, [
    detailDraft.customerNumber,
    detailDraft.fileId,
    detailDraft.originalFileName,
    duplicateInvoiceNumber,
    duplicatingDraft,
    globalData?.userId,
    handleSelectDraft,
    selectedDetailCustomer,
  ]);

  const clearSplitPagePreviews = useCallback(() => {
    setSplitPagePreviews((previousPreviews) => {
      Object.values(previousPreviews).forEach((previewUrl) => {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      });
      return {};
    });
  }, []);

  const loadSplitPagePreviews = useCallback(async (sourcePdfBlob, pageCount) => {
    if (!sourcePdfBlob || !pageCount) {
      clearSplitPagePreviews();
      return;
    }

    const runId = splitPreviewRunRef.current + 1;
    splitPreviewRunRef.current = runId;
    setLoadingSplitPagePreviews(true);
    clearSplitPagePreviews();

    let nextPreviews = {};

    try {
      const sourceBytes = await sourcePdfBlob.arrayBuffer();
      const sourcePdfDocument = await PDFDocument.load(sourceBytes);
      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        const singlePageDocument = await PDFDocument.create();
        const [copiedPage] = await singlePageDocument.copyPages(sourcePdfDocument, [pageNumber - 1]);
        singlePageDocument.addPage(copiedPage);
        const singlePageBytes = await singlePageDocument.save();
        const previewBlob = new Blob([singlePageBytes], { type: "application/pdf" });
        nextPreviews[pageNumber] = URL.createObjectURL(previewBlob);
      }

      if (splitPreviewRunRef.current !== runId) {
        Object.values(nextPreviews).forEach((previewUrl) => {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
        });
        nextPreviews = {};
        return;
      }

      if (splitPreviewRunRef.current === runId) {
        setSplitPagePreviews((previousPreviews) => {
          Object.values(previousPreviews).forEach((previewUrl) => {
            if (previewUrl) {
              URL.revokeObjectURL(previewUrl);
            }
          });
          return nextPreviews;
        });
      }
    } catch (error) {
      if (splitPreviewRunRef.current === runId) {
        Object.values(nextPreviews).forEach((previewUrl) => {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
        });
        nextPreviews = {};
        clearSplitPagePreviews();
        setSnackbar({
          open: true,
          severity: "warning",
          message: "Page previews could not be generated. You can still select pages manually.",
        });
      }
    } finally {
      if (splitPreviewRunRef.current === runId) {
        setLoadingSplitPagePreviews(false);
      }
    }
  }, [clearSplitPagePreviews]);

  const handleOpenSplitDialog = useCallback(async () => {
    if (!selectedDraftId || splittingDraft) {
      return;
    }

    const trimmedCustomerNumber = String(detailDraft.customerNumber || "").trim();
    if (!trimmedCustomerNumber) {
      setSnackbar({ open: true, severity: "warning", message: "Customer number is required to split a PDF." });
      return;
    }

    if (!selectedDetailCustomer) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Enter a valid customer number before splitting.",
      });
      return;
    }

    if (!detailDraft.fileId) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "This draft has no PDF to split.",
      });
      return;
    }

    try {
      let sourcePdfBlob = pdfBlob;
      if (!sourcePdfBlob) {
        const sourceFileResponse = await axios.get(`/api/v1/azureblobstorage/${detailDraft.fileId}`, { responseType: "blob" });
        sourcePdfBlob = sourceFileResponse.data;
        setPdfBlob(sourcePdfBlob);
      }

      const sourceBytes = await sourcePdfBlob.arrayBuffer();
      const sourcePdfDocument = await PDFDocument.load(sourceBytes);
      const pageCount = sourcePdfDocument.getPageCount();

      if (!pageCount) {
        setSnackbar({ open: true, severity: "warning", message: "The selected PDF has no pages." });
        return;
      }

      setSplitPageCount(pageCount);
      setSelectedSplitPages([]);
      setActiveSplitPreviewPage(1);
      setRemoveSelectedPagesFromCurrentDraft(true);
      setIsSplitDialogOpen(true);
      void loadSplitPagePreviews(sourcePdfBlob, pageCount);
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Unable to prepare PDF splitting.",
      });
    }
  }, [
    detailDraft.customerNumber,
    detailDraft.fileId,
    loadSplitPagePreviews,
    pdfBlob,
    selectedDetailCustomer,
    selectedDraftId,
    splittingDraft,
  ]);

  const handleCloseSplitDialog = useCallback(() => {
    if (splittingDraft) {
      return;
    }

    setIsSplitDialogOpen(false);
    setSplitPageCount(0);
    setSelectedSplitPages([]);
    setActiveSplitPreviewPage(1);
    clearSplitPagePreviews();
    setLoadingSplitPagePreviews(false);
    splitPreviewRunRef.current += 1;
    setRemoveSelectedPagesFromCurrentDraft(true);
  }, [clearSplitPagePreviews, splittingDraft]);

  const handleToggleSplitPage = useCallback((pageNumber) => {
    setSelectedSplitPages((prev) => {
      if (prev.includes(pageNumber)) {
        return prev.filter((value) => value !== pageNumber);
      }

      return [...prev, pageNumber].sort((a, b) => a - b);
    });
  }, []);

  const handleConfirmSplitDraft = useCallback(async () => {
    const trimmedCustomerNumber = String(detailDraft.customerNumber || "").trim();
    const trimmedInvoiceNumber = String(detailDraft.invoiceNumber || "").trim();
    if (!trimmedCustomerNumber || !selectedDetailCustomer || !detailDraft.fileId || splittingDraft) {
      setIsSplitDialogOpen(false);
      return;
    }

    if (!selectedSplitPages.length) {
      setSnackbar({ open: true, severity: "warning", message: "Select at least one page to split." });
      return;
    }

    try {
      setSplittingDraft(true);

      let sourcePdfBlob = pdfBlob;
      if (!sourcePdfBlob) {
        const sourceFileResponse = await axios.get(`/api/v1/azureblobstorage/${detailDraft.fileId}`, { responseType: "blob" });
        sourcePdfBlob = sourceFileResponse.data;
        setPdfBlob(sourcePdfBlob);
      }

      const sourceBytes = await sourcePdfBlob.arrayBuffer();
      const sourcePdfDocument = await PDFDocument.load(sourceBytes);
      const sourcePageCount = sourcePdfDocument.getPageCount();
      const uniqueSelectedPages = [...new Set(selectedSplitPages)]
        .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber > 0 && pageNumber <= sourcePageCount)
        .sort((a, b) => a - b);

      if (!uniqueSelectedPages.length) {
        throw new Error("No valid pages selected.");
      }

      if (removeSelectedPagesFromCurrentDraft && uniqueSelectedPages.length === sourcePageCount) {
        setSnackbar({
          open: true,
          severity: "warning",
          message: "At least one page must remain in the current draft PDF.",
        });
        return;
      }

      const splitPdfDocument = await PDFDocument.create();
      const copiedPages = await splitPdfDocument.copyPages(
        sourcePdfDocument,
        uniqueSelectedPages.map((pageNumber) => pageNumber - 1)
      );
      copiedPages.forEach((page) => splitPdfDocument.addPage(page));

      const splitPdfBytes = await splitPdfDocument.save();
      const splitPdfBlob = new Blob([splitPdfBytes], { type: "application/pdf" });
      const splitFileName = buildSplitFileName(detailDraft.originalFileName || "manual-invoice.pdf");

      const splitFileInfoResponse = await axios.post("/api/v1/file", {
        documentType: 1,
        creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        createdBy: globalData?.userId || 1,
        fileName: splitFileName,
        fileSize: splitPdfBlob.size,
        mimeType: splitPdfBlob.type || "application/pdf",
      });
      const splitFileId = splitFileInfoResponse?.data?.fileId;
      if (!splitFileId) {
        throw new Error("Failed to create split file metadata.");
      }

      const splitFormData = new FormData();
      splitFormData.append("file", splitPdfBlob, splitFileId);
      await axios.post("/api/v1/azureblobstorage", splitFormData);

      const splitDraftResponse = await axios.post("/api/v1/manualInvoiceDrafts/split", {
        sourceDraftId: selectedDraftId,
        customerNumber: trimmedCustomerNumber,
        baseInvoiceNumber: trimmedInvoiceNumber || null,
        fileId: splitFileId,
        originalFileName: splitFileName,
      });
      const splitDraft = splitDraftResponse?.data;
      if (!splitDraft?.manualInvoiceDraftId) {
        throw new Error("Missing split draft identifier.");
      }

      let currentDraftUpdated = false;
      if (removeSelectedPagesFromCurrentDraft) {
        const selectedPageSet = new Set(uniqueSelectedPages);
        const remainingPages = Array.from({ length: sourcePageCount }, (_, index) => index + 1).filter(
          (pageNumber) => !selectedPageSet.has(pageNumber)
        );

        if (!remainingPages.length) {
          throw new Error("No remaining pages available for current draft.");
        }

        const remainingPdfDocument = await PDFDocument.create();
        const remainingCopiedPages = await remainingPdfDocument.copyPages(
          sourcePdfDocument,
          remainingPages.map((pageNumber) => pageNumber - 1)
        );
        remainingCopiedPages.forEach((page) => remainingPdfDocument.addPage(page));

        const remainingPdfBytes = await remainingPdfDocument.save();
        const remainingPdfBlob = new Blob([remainingPdfBytes], { type: "application/pdf" });
        const remainingFileName = buildRemainingFileName(detailDraft.originalFileName || "manual-invoice.pdf");

        const remainingFileInfoResponse = await axios.post("/api/v1/file", {
          documentType: 1,
          creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
          createdBy: globalData?.userId || 1,
          fileName: remainingFileName,
          fileSize: remainingPdfBlob.size,
          mimeType: remainingPdfBlob.type || "application/pdf",
        });
        const remainingFileId = remainingFileInfoResponse?.data?.fileId;
        if (!remainingFileId) {
          throw new Error("Failed to create remaining file metadata.");
        }

        const remainingFormData = new FormData();
        remainingFormData.append("file", remainingPdfBlob, remainingFileId);
        await axios.post("/api/v1/azureblobstorage", remainingFormData);

        await axios.put(`/api/v1/manualInvoiceDrafts/${selectedDraftId}`, {
          fileId: remainingFileId,
        });

        setDrafts((prev) =>
          prev.map((item) =>
            item.manualInvoiceDraftId === selectedDraftId
              ? {
                  ...item,
                  fileId: remainingFileId,
                  lastUpdated: new Date().toISOString(),
                }
              : item
          )
        );

        currentDraftUpdated = true;
      }

      setDrafts((prev) => [splitDraft, ...prev]);
      handleSelectDraft(splitDraft);
      setIsSplitDialogOpen(false);
      setSplitPageCount(0);
      setSelectedSplitPages([]);
      setActiveSplitPreviewPage(1);
      clearSplitPagePreviews();
      setLoadingSplitPagePreviews(false);
      splitPreviewRunRef.current += 1;
      setRemoveSelectedPagesFromCurrentDraft(true);
      setSnackbar({
        open: true,
        severity: "success",
        message: currentDraftUpdated
          ? "Split draft created and selected pages removed from current draft PDF."
          : "PDF pages saved as new draft.",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Unable to split PDF into a new draft.",
      });
    } finally {
      setSplittingDraft(false);
    }
  }, [
    detailDraft.customerNumber,
    detailDraft.fileId,
    detailDraft.invoiceNumber,
    detailDraft.originalFileName,
    globalData?.userId,
    handleSelectDraft,
    pdfBlob,
    selectedDetailCustomer,
    selectedDraftId,
    selectedSplitPages,
    clearSplitPagePreviews,
    removeSelectedPagesFromCurrentDraft,
    splittingDraft,
  ]);

  const handleOpenDraftHistoryFile = useCallback(async (fileId) => {
    if (!fileId) {
      return;
    }

    try {
      const response = await axios.get(`/api/v1/azureblobstorage/${fileId}`, { responseType: "blob" });
      const fileUrl = URL.createObjectURL(response.data);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Unable to open previous file.",
      });
    }
  }, []);

  const handleRestoreOldFile = useCallback(
    async (fileId) => {
      if (!selectedDraftId || !fileId || restoringOldFileId) {
        return;
      }

      try {
        setRestoringOldFileId(fileId);
        await axios.put(`/api/v1/manualInvoiceDrafts/${selectedDraftId}`, { fileId });
        setDrafts((prev) =>
          prev.map((item) =>
            item.manualInvoiceDraftId === selectedDraftId
              ? {
                  ...item,
                  fileId,
                  lastUpdated: new Date().toISOString(),
                }
              : item
          )
        );
        setDetailDraft((prev) => ({
          ...prev,
          fileId,
        }));
        await loadDraftFileHistory(selectedDraftId);
        setSnackbar({ open: true, severity: "success", message: "Old file restored for this draft." });
      } catch (error) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Unable to restore old file.",
        });
      } finally {
        setRestoringOldFileId(null);
      }
    },
    [loadDraftFileHistory, restoringOldFileId, selectedDraftId]
  );

  useEffect(() => {
    const trimmedCustomerNumber = String(detailDraft.customerNumber || "").trim();
    const trimmedInvoiceNumber = String(detailDraft.invoiceNumber || "").trim();

    if (!selectedDraftId || !trimmedCustomerNumber || !trimmedInvoiceNumber) {
      setInvoiceNumberInfo("");
      return;
    }

    const debounceId = setTimeout(() => {
      checkInvoiceNumberDuplicate();
    }, 250);

    return () => clearTimeout(debounceId);
  }, [checkInvoiceNumberDuplicate, detailDraft.customerNumber, detailDraft.invoiceNumber, selectedDraftId]);

  const productTypeOptions = useMemo(() => {
    if (!detailDraft.countryCode) {
      return [];
    }

    const uniqueByCode = new Map();
    productData
      .filter((item) => item.countryCode === detailDraft.countryCode)
      .forEach((item) => {
        if (!uniqueByCode.has(item.categoryCode)) {
          uniqueByCode.set(item.categoryCode, item.productShortDescription || item.categoryCode);
        }
      });

    return Array.from(uniqueByCode.entries())
      .map(([code, label]) => ({ code, label }))
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" }));
  }, [detailDraft.countryCode, productData]);

  const productSubCodeOptions = useMemo(() => {
    if (!detailDraft.countryCode || !detailDraft.claimProductTypeCode) {
      return [];
    }

    let subCodeRows = productData
      .filter(
        (item) => item.countryCode === detailDraft.countryCode && String(item.categoryCode) === String(detailDraft.claimProductTypeCode)
      )
      .map((item) => ({
        subCode: String(item.subCode ?? ""),
        label: item.productLongDescription || item.productShortDescription || "",
        productTypeId: item.productTypeId,
      }));

    const hasEmptySubCode = subCodeRows.some((item) => item.subCode === "");

    if (subCodeRows.length === 0 || (subCodeRows.length === 1 && hasEmptySubCode)) {
      return [{ subCode: "98", label: "98. N/A", productTypeId: subCodeRows[0]?.productTypeId || null }];
    }

    if (hasEmptySubCode) {
      subCodeRows = subCodeRows.filter((item) => item.subCode !== "");
      subCodeRows.push({
        subCode: "98",
        label: "98. N/A",
        productTypeId: productData.find(
          (item) =>
            item.countryCode === detailDraft.countryCode &&
            String(item.categoryCode) === String(detailDraft.claimProductTypeCode) &&
            String(item.subCode ?? "") === ""
        )?.productTypeId,
      });
    }

    const uniqueBySubCode = new Map();
    subCodeRows.forEach((item) => {
      if (!uniqueBySubCode.has(item.subCode)) {
        const displayLabel = item.subCode === "98" ? "98. N/A" : `${item.subCode} - ${item.label}`;
        uniqueBySubCode.set(item.subCode, {
          subCode: item.subCode,
          label: displayLabel,
          productTypeId: item.productTypeId,
        });
      }
    });

    return sortSubCodeOptions(Array.from(uniqueBySubCode.values()));
  }, [detailDraft.claimProductTypeCode, detailDraft.countryCode, productData]);

  useEffect(() => {
    if (noteOverlayPosition.left !== null) {
      return;
    }

    const overlayWidth = 350;
    const centeredLeft = Math.max(8, Math.round((window.innerWidth - overlayWidth) / 2));
    setNoteOverlayPosition((prev) => ({ ...prev, left: centeredLeft }));
  }, [noteOverlayPosition.left]);

  useEffect(() => {
    if (!isNoteOverlayDragging) {
      return;
    }

    const handleMouseMove = (event) => {
      const overlayWidth = 350;
      const minPadding = 8;
      const nextLeftRaw = event.clientX - noteOverlayDragOffsetRef.current.x;
      const nextTopRaw = event.clientY - noteOverlayDragOffsetRef.current.y;
      const maxLeft = Math.max(minPadding, window.innerWidth - overlayWidth - minPadding);

      setNoteOverlayPosition({
        left: Math.min(maxLeft, Math.max(minPadding, nextLeftRaw)),
        top: Math.max(minPadding, nextTopRaw),
      });
    };

    const handleMouseUp = () => {
      setIsNoteOverlayDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isNoteOverlayDragging]);

  const handleStartDraggingNoteOverlay = (event) => {
    if (event.button !== 0) {
      return;
    }

    const overlayElement = event.currentTarget.parentElement;
    if (!overlayElement) {
      return;
    }

    const overlayRect = overlayElement.getBoundingClientRect();
    noteOverlayDragOffsetRef.current = {
      x: event.clientX - overlayRect.left,
      y: event.clientY - overlayRect.top,
    };
    setIsNoteOverlayDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isClipboardOverlayDragging) {
      return;
    }

    const handleMouseMove = (event) => {
      const overlayWidth = 760;
      const minPadding = 8;
      const minLeft = Math.max(minPadding, minClipboardOverlayLeft);
      const nextLeftRaw = event.clientX - clipboardOverlayDragOffsetRef.current.x;
      const nextTopRaw = event.clientY - clipboardOverlayDragOffsetRef.current.y;
      const maxLeft = Math.max(minPadding, window.innerWidth - overlayWidth - minPadding);

      setClipboardOverlayPosition({
        left: Math.min(maxLeft, Math.max(minLeft, nextLeftRaw)),
        top: Math.max(minPadding, nextTopRaw),
      });
    };

    const handleMouseUp = () => {
      setIsClipboardOverlayDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isClipboardOverlayDragging]);

  const handleStartDraggingClipboardOverlay = (event) => {
    if (isClipboardOverlayMinimized) {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    const overlayElement = event.currentTarget.parentElement;
    if (!overlayElement) {
      return;
    }

    const overlayRect = overlayElement.getBoundingClientRect();
    clipboardOverlayDragOffsetRef.current = {
      x: event.clientX - overlayRect.left,
      y: event.clientY - overlayRect.top,
    };
    setIsClipboardOverlayDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!selectedDraftId || !detailDraft.supplierId || !detailDraft.claimProductTypeCode || !detailDraft.claimProductSubCode) {
      setMatchedSupplierNotes([]);
      return;
    }

    let isCancelled = false;
    const fetchMatchedNotes = async () => {
      try {
        const response = await axios.get("/api/v1/supplierProductCombinationNotes/match", {
          params: {
            supplierId: detailDraft.supplierId,
            categoryCode: String(detailDraft.claimProductTypeCode),
            productSubCode: normalizeSubCode(detailDraft.claimProductSubCode),
          },
        });

        if (!isCancelled) {
          setMatchedSupplierNotes(response.data ?? []);
        }
      } catch (error) {
        if (!isCancelled) {
          setMatchedSupplierNotes([]);
        }
      }
    };

    fetchMatchedNotes();

    return () => {
      isCancelled = true;
    };
  }, [
    detailDraft.claimProductSubCode,
    detailDraft.claimProductTypeCode,
    detailDraft.supplierId,
    selectedDraftId,
  ]);

  const uploadFileToDraft = useCallback(
    async (fileObj) => {
      const { file } = fileObj;
      const trimmedCustomerNumber = String(fileObj.customerNumber || "").trim();
      if (!trimmedCustomerNumber) {
        throw new Error("Customer number is missing for this upload.");
      }

      const fileInfo = {
        documentType: 1,
        creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        createdBy: globalData?.userId || 1,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || "application/pdf",
      };
      const fileResponse = await axios.post("/api/v1/file", fileInfo);
      const fileId = fileResponse.data.fileId;

      const formData = new FormData();
      formData.append("file", file, fileId);
      await axios.post("/api/v1/azureblobstorage", formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadFiles((prevFiles) =>
            prevFiles.map((item) => (item.id === fileObj.id ? { ...item, progress, uploading: true } : item))
          );
        },
      });

      const draftResponse = await axios.post("/api/v1/manualInvoiceDrafts", {
        originalFileName: file.name,
        fileId,
        customerNumber: trimmedCustomerNumber,
      });

      return draftResponse.data;
    },
    [globalData?.userId]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!selectedUploadCustomerNumber) {
        setSnackbar({ open: true, severity: "warning", message: "Select a customer before adding PDF files." });
        return;
      }

      if (!acceptedFiles?.length) {
        return;
      }

      const selectedEntity = entityData.find((entity) => entity.entityNumber === selectedUploadCustomerNumber);
      const selectedCustomerName = String(selectedEntity?.name || "").trim();
      const selectedCustomerLabel = selectedCustomerName
        ? `${selectedUploadCustomerNumber} - ${selectedCustomerName}`
        : selectedUploadCustomerNumber;
      const nextFiles = acceptedFiles.map((file) => ({
        file,
        id: `${Date.now()}-${Math.random()}`,
        customerNumber: selectedUploadCustomerNumber,
        customerLabel: selectedCustomerLabel,
        uploading: false,
        uploaded: false,
        progress: 0,
        size: file.size,
        type: file.type || "application/pdf",
        error: file.size > 5500000 ? "File is too large, max allowed file size is 5.5MB" : null,
      }));

      setUploadFiles((prevFiles) => [...prevFiles, ...nextFiles]);
    },
    [entityData, selectedUploadCustomerNumber]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [] },
    multiple: true,
    disabled: uploading || !selectedUploadCustomerNumber,
  });

  const handleRemoveUploadFile = useCallback((fileId) => {
    if (uploading) {
      return;
    }

    setUploadFiles((prevFiles) => prevFiles.filter((fileObj) => fileObj.id !== fileId));
  }, [uploading]);

  const handleUploadManualFiles = useCallback(async () => {
    if (!selectedUploadCustomerNumber) {
      setSnackbar({ open: true, severity: "warning", message: "Select a customer before uploading PDF files." });
      return;
    }

    const filesToUpload = uploadFiles.filter((fileObj) => !fileObj.uploaded && !fileObj.error);
    if (filesToUpload.length === 0) {
      setSnackbar({ open: true, severity: "warning", message: "Add at least one valid PDF file to upload." });
      return;
    }

    if (filesToUpload.some((fileObj) => !String(fileObj.customerNumber || "").trim())) {
      setSnackbar({ open: true, severity: "error", message: "One or more files are missing a customer number." });
      return;
    }

    setUploading(true);
    try {
      const uploadedDrafts = [];
      for (const fileObj of filesToUpload) {
        try {
          setUploadFiles((prevFiles) =>
            prevFiles.map((item) => (item.id === fileObj.id ? { ...item, uploading: true, error: null } : item))
          );
          const draft = await uploadFileToDraft(fileObj);
          uploadedDrafts.push(draft);
          setUploadFiles((prevFiles) =>
            prevFiles.map((item) =>
              item.id === fileObj.id ? { ...item, progress: 100, uploading: false, uploaded: true, error: null } : item
            )
          );
        } catch (error) {
          setUploadFiles((prevFiles) =>
            prevFiles.map((item) =>
              item.id === fileObj.id
                ? { ...item, uploading: false, error: error.response?.data || error.message || "Upload failed." }
                : item
            )
          );
        }
      }

      if (uploadedDrafts.length > 0) {
        setDrafts((prev) => [...uploadedDrafts, ...prev]);
        setSnackbar({
          open: true,
          severity: "success",
          message: `${uploadedDrafts.length} PDF file(s) added as manual drafts.`,
        });
        handleSelectDraft(uploadedDrafts[0]);
      } else {
        setSnackbar({ open: true, severity: "error", message: "Failed to upload one or more PDF files." });
      }
    } finally {
      setUploading(false);
    }
  }, [handleSelectDraft, selectedUploadCustomerNumber, uploadFileToDraft, uploadFiles]);

  const handleOpenBatchDialog = () => {
    setIsBatchDialogOpen(true);
  };

  const handleCloseBatchDialog = () => {
    if (uploading) {
      return;
    }

    setIsBatchDialogOpen(false);
  };

  const loadHistoricUploads = useCallback(
    async (filters = historicUploadFilters) => {
      const params = Object.entries(filters).reduce((activeParams, [key, value]) => {
        const formattedValue = formatHistoricUploadDateForApi(value);
        if (!formattedValue) {
          return activeParams;
        }

        return {
          ...activeParams,
          [key]: formattedValue,
        };
      }, {});

      setLoadingHistoricUploads(true);
      try {
        const response = await axios.get("/api/v1/manualInvoiceDrafts/historicUploads", { params });
        const processedHistoricUploads = (response.data ?? []).filter((item) => Number(item.processedByDWS || 0) === 1);
        setHistoricUploads(processedHistoricUploads);
      } catch (error) {
        setSnackbar({ open: true, severity: "error", message: "Unable to load historic uploads." });
      } finally {
        setLoadingHistoricUploads(false);
      }
    },
    [historicUploadFilters]
  );

  const handleOpenHistoricUploadsDialog = useCallback(() => {
    setIsHistoricUploadsDialogOpen(true);
    if (!showHistoricUploadRows) {
      setHistoricUploads([]);
      return;
    }

    loadHistoricUploads();
  }, [loadHistoricUploads]);

  const handleHistoricUploadFilterChange = useCallback((field, value, keyboardInputValue) => {
    setHistoricUploadFilters((prevFilters) => {
      const nextFilters = {
        ...prevFilters,
        [field]: value,
      };
      const canAutoFillToDate =
        isCompleteHistoricUploadDateInput(keyboardInputValue) && isValidHistoricUploadDate(value);

      if (field === "createdDateFrom" && canAutoFillToDate && !prevFilters.createdDateTo) {
        nextFilters.createdDateTo = value;
      }

      if (field === "lastUpdatedFrom" && canAutoFillToDate && !prevFilters.lastUpdatedTo) {
        nextFilters.lastUpdatedTo = value;
      }

      return nextFilters;
    });
  }, []);

  const handleApplyHistoricUploadFilters = useCallback(() => {
    loadHistoricUploads(historicUploadFilters);
  }, [historicUploadFilters, loadHistoricUploads]);

  const handleResetHistoricUploadFilters = useCallback(() => {
    setHistoricUploadFilters(emptyHistoricUploadFilters);
    loadHistoricUploads(emptyHistoricUploadFilters);
  }, [loadHistoricUploads]);

  const isHistoricUploadToDateBeforeFromDate = useCallback(
    (fromField, toField) => {
      const fromDate = getHistoricUploadDateMoment(historicUploadFilters[fromField]);
      const toDate = getHistoricUploadDateMoment(historicUploadFilters[toField]);

      return Boolean(fromDate && toDate && toDate.isBefore(fromDate, "day"));
    },
    [historicUploadFilters]
  );

  const getHistoricUploadDateFieldError = useCallback(
    (field, inputValue) => {
      if (isInvalidCompleteHistoricUploadDateInput(inputValue)) {
        return true;
      }

      if (field === "createdDateTo") {
        return isHistoricUploadToDateBeforeFromDate("createdDateFrom", "createdDateTo");
      }

      if (field === "lastUpdatedTo") {
        return isHistoricUploadToDateBeforeFromDate("lastUpdatedFrom", "lastUpdatedTo");
      }

      return false;
    },
    [isHistoricUploadToDateBeforeFromDate]
  );

  const validateDraftBeforePublish = useCallback(
    (draft) => {
      if (!draft.customerNumber || !entityData.some((entity) => entity.entityNumber === draft.customerNumber)) {
        return "Customer number is missing or invalid.";
      }

      if (!draft.invoiceNumber || !draft.invoiceDate) {
        return "Invoice number and date are required.";
      }

      if (!draft.supplierId || !draft.countryCode || !draft.claimProductTypeId || !draft.currencyId) {
        return "Supplier, country, product type, and currency are required.";
      }

      if (parseNumberOrNull(draft.invoiceGrossAmount) === null || parseNumberOrNull(draft.invoiceVATAmountLocalCurrency) === null) {
        return "Net and VAT amounts are required.";
      }

      if (isDuplicateInvoiceForCustomer(draft)) {
        return "Invoice number already exists for this customer.";
      }

      return "";
    },
    [entityData, isDuplicateInvoiceForCustomer]
  );

  const isDraftSelectable = useCallback(
    (draft) => {
      if (isDuplicateInvoiceForCustomer(draft)) {
        return false;
      }

      return validateDraftBeforePublish(draft) === "";
    },
    [isDuplicateInvoiceForCustomer, validateDraftBeforePublish]
  );

  const isGridRowSelectable = useCallback(
    (draft) => {
      if (selectionEnabled) {
        return true;
      }

      return isDraftSelectable(draft);
    },
    [isDraftSelectable, selectionEnabled]
  );

  const handleOpenPublishDialog = () => {
    if (selectionModel.length === 0) {
      setSnackbar({ open: true, severity: "warning", message: "Select at least one draft to publish." });
      return;
    }

    const selectedDrafts = drafts.filter((item) => selectionModel.includes(item.manualInvoiceDraftId));
    const validationErrors = selectedDrafts
      .map((draft) => ({
        id: draft.manualInvoiceDraftId,
        error: validateDraftBeforePublish(draft),
      }))
      .filter((item) => item.error);

    if (validationErrors.length > 0) {
      setSnackbar({
        open: true,
        severity: "error",
        message: `Draft ${validationErrors[0].id}: ${validationErrors[0].error}`,
      });
      return;
    }

    setPublishRows(
      selectedDrafts.map((draft) => ({
        id: draft.manualInvoiceDraftId,
        customerNumber: draft.customerNumber || "",
        fileName: draft.originalFileName || "",
        invoiceNumber: draft.invoiceNumber || "",
        progress: 0,
        publishing: false,
        published: false,
        error: null,
      }))
    );
    setIsPublishDialogOpen(true);
  };

  const handleClosePublishDialog = () => {
    if (publishing) {
      return;
    }

    setIsPublishDialogOpen(false);
  };

  const updatePublishRow = (draftId, nextValues) => {
    setPublishRows((prevRows) => prevRows.map((row) => (row.id === draftId ? { ...row, ...nextValues } : row)));
  };

  const handlePublishSelected = async () => {
    const rowsToPublish = publishRows.filter((row) => !row.published && !row.error);
    if (rowsToPublish.length === 0) {
      return;
    }

    setPublishing(true);
    const publishedIds = [];
    const failedItems = [];

    try {
      for (const row of rowsToPublish) {
        try {
          updatePublishRow(row.id, { publishing: true, error: null, progress: 25 });
          const response = await axios.post("/api/v1/manualInvoiceDrafts/publish", {
            draftIds: [row.id],
          });

          const rowPublishedIds = response.data?.publishedIds || [];
          const rowFailedItems = response.data?.failed || [];

          if (rowPublishedIds.includes(row.id)) {
            publishedIds.push(row.id);
            updatePublishRow(row.id, { publishing: false, published: true, progress: 100 });
          } else {
            const errorMessage = rowFailedItems[0]?.error || "Failed to add draft to database.";
            failedItems.push({ draftId: row.id, error: errorMessage });
            updatePublishRow(row.id, { publishing: false, error: errorMessage, progress: 100 });
          }
        } catch (error) {
          const errorMessage = error.response?.data || error.message || "Failed to add draft to database.";
          failedItems.push({ draftId: row.id, error: errorMessage });
          updatePublishRow(row.id, { publishing: false, error: errorMessage, progress: 100 });
        }
      }

      setDrafts((prev) => prev.filter((item) => !publishedIds.includes(item.manualInvoiceDraftId)));
      setSelectionModel((prev) => prev.filter((id) => !publishedIds.includes(id)));

      if (selectedDraftId && publishedIds.includes(selectedDraftId)) {
        setSelectedDraftId(null);
        setDetailDraft(emptyDraft);
        setPdfUrl("");
      }

      if (failedItems.length > 0) {
        setSnackbar({
          open: true,
          severity: "warning",
          message: `${publishedIds.length} published, ${failedItems.length} failed.`,
        });
      } else {
        setSnackbar({
          open: true,
          severity: "success",
          message: `${publishedIds.length} draft invoice(s) added to database.`,
        });
      }
    } catch (error) {
      setSnackbar({ open: true, severity: "error", message: "Failed to publish selected drafts." });
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteDraft = useCallback((draftId) => {
    setDraftIdsToDelete([draftId]);
  }, []);

  const handleDeleteSelectedDrafts = useCallback(() => {
    if (selectionModel.length === 0) {
      setSnackbar({ open: true, severity: "warning", message: "Select at least one draft to delete." });
      return;
    }

    setDraftIdsToDelete(selectionModel);
  }, [selectionModel]);

  const handleCloseDeleteDraftDialog = useCallback(() => {
    if (deletingDraft) {
      return;
    }

    setDraftIdsToDelete([]);
  }, [deletingDraft]);

  const handleConfirmDeleteDraft = useCallback(async () => {
    if (draftIdsToDelete.length === 0) {
      return;
    }

    const draftIds = [...draftIdsToDelete];
    setDeletingDraft(true);
    try {
      let deletedIds = [];
      let failedItems = [];

      if (draftIds.length === 1) {
        await axios.delete(`/api/v1/manualInvoiceDrafts/${draftIds[0]}`);
        deletedIds = draftIds;
      } else {
        const response = await axios.post("/api/v1/manualInvoiceDrafts/delete", {
          draftIds,
        });
        deletedIds = response.data?.deletedIds || [];
        failedItems = response.data?.failed || [];
      }

      setDrafts((prev) => prev.filter((item) => !deletedIds.includes(item.manualInvoiceDraftId)));
      setSelectionModel((prev) => prev.filter((id) => !deletedIds.includes(id)));

      if (selectedDraftId && deletedIds.includes(selectedDraftId)) {
        setSelectedDraftId(null);
        setDetailDraft(emptyDraft);
        setPdfUrl("");
        setPdfBlob(null);
        setCustomerNumberError("");
        setInvoiceNumberInfo("");
      }

      if (failedItems.length > 0) {
        setSnackbar({
          open: true,
          severity: "warning",
          message: `${deletedIds.length} deleted, ${failedItems.length} failed.`,
        });
      } else {
        setSnackbar({
          open: true,
          severity: "success",
          message: `${deletedIds.length} draft invoice(s) deleted.`,
        });
      }
      setDraftIdsToDelete([]);
    } catch (error) {
      setSnackbar({ open: true, severity: "error", message: "Failed to delete draft invoice(s)." });
    } finally {
      setDeletingDraft(false);
    }
  }, [draftIdsToDelete, selectedDraftId]);

  const columns = [
    { field: "manualInvoiceDraftId", headerName: "Draft ID", width: 70, hide: true },
    { field: "customerNumber", headerName: "Customer", width: 90 },
    { field: "invoiceNumber", headerName: "Invoice number", width: 170 },
    {
      field: "invoiceDate",
      headerName: "Invoice date",
      width: 110,
      valueFormatter: (params) => (params.value ? moment(params.value).format("DD-MM-YYYY") : ""),
      renderCell: (params) => {
        if (!params.value) {
          return "";
        }

        const formatted = moment(params.value).format("DD-MM-YYYY");
        const isFuture = moment(params.value).startOf("day").isAfter(moment().startOf("day"));

        return isFuture ? (
          <Tooltip title="Invoice date is in the future">
            <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>
              {formatted}
            </Box>
          </Tooltip>
        ) : (
          formatted
        );
      },
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      minWidth: 160,
      flex: 1,
      valueGetter: (params) =>
        mergedSupplierData.find((supplier) => supplier.supplierId === params.row.supplierId)?.supplierName || "",
    },
    {
      field: "countryName",
      headerName: "Country",
      width: 90,
      valueGetter: (params) =>
        countryData.find((country) => country.countryId === params.row.countryCode)?.countryCode || "",
    },
    {
      field: "productCode",
      headerName: "Product code",
      width: 115,
      valueGetter: (params) => {
        const product = productData.find((item) => item.productTypeId === params.row.claimProductTypeId);
        return formatProductCodeDisplay(product?.categoryCode, product?.subCode);
      },
    },
    {
      field: "currencyCode",
      headerName: "Currency",
      width: 90,
      valueGetter: (params) =>
        currencyData.find((currency) => currency.currencyId === params.row.currencyId)?.isoCode || "",
    },
    {
      field: "invoiceGrossAmount",
      headerName: "Net amount",
      width: 110,
      valueFormatter: (params) => (params.value === null || params.value === undefined ? "" : Number(params.value).toFixed(2)),
    },
    {
      field: "invoiceVATAmountLocalCurrency",
      headerName: "VAT amount",
      width: 110,
      valueFormatter: (params) => (params.value === null || params.value === undefined ? "" : Number(params.value).toFixed(2)),
    },
    {
      field: "actions",
      headerName: "",
      width: 35,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Delete draft">
          <IconButton
            size="small"
            aria-label="Delete draft"
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteDraft(params.row.manualInvoiceDraftId);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const sortedDraftRows = useMemo(
    () => [...drafts].sort((a, b) => Number(a.manualInvoiceDraftId || 0) - Number(b.manualInvoiceDraftId || 0)),
    [drafts]
  );
  const customerFilterOptions = useMemo(() => {
    const invoiceCountsByCustomerNumber = drafts.reduce((counts, draft) => {
      const customerNumber = String(draft.customerNumber || "").trim();
      if (!customerNumber) {
        return counts;
      }

      counts.set(customerNumber, (counts.get(customerNumber) || 0) + 1);
      return counts;
    }, new Map());

    const customerNumbers = Array.from(invoiceCountsByCustomerNumber.keys()).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );

    return customerNumbers.map((customerNumber) => {
      const customer = entityData.find((entity) => entity.entityNumber === customerNumber);
      const customerName = customer?.name ? ` - ${customer.name}` : "";

      return {
        customerNumber,
        invoiceCount: invoiceCountsByCustomerNumber.get(customerNumber) || 0,
        label: `${customerNumber}${customerName}`,
      };
    });
  }, [drafts, entityData]);
  const detailCustomerName = useMemo(() => {
    const customerNumber = String(detailDraft.customerNumber || "").trim();
    if (!customerNumber) {
      return "";
    }

    return entityData.find((entity) => entity.entityNumber === customerNumber)?.name || "";
  }, [detailDraft.customerNumber, entityData]);
  const detailCountryName = useMemo(() => {
    if (!detailDraft.countryCode) {
      return "";
    }

    const selectedCountry = countryData.find((country) => country.countryId === detailDraft.countryCode);
    return selectedCountry?.countryName || "";
  }, [countryData, detailDraft.countryCode]);
  const invoiceDateHelperText = useMemo(() => {
    if (!detailDraft.invoiceDate) {
      return " ";
    }

    const parsedInvoiceDate = moment(detailDraft.invoiceDate).startOf("day");
    if (!parsedInvoiceDate.isValid()) {
      return " ";
    }

    const today = moment().startOf("day");
    if (parsedInvoiceDate.isAfter(today)) {
      return "Invoice date is in the future";
    }

    const quarterDeadline = parsedInvoiceDate.endOf("quarter").add(45, "days").startOf("day");
    if (today.isAfter(quarterDeadline)) {
      return "Invoice date falls outside claim period";
    }

    return " ";
  }, [detailDraft.invoiceDate]);
  const hasInvoiceDateWarning = invoiceDateHelperText.trim().length > 0;
  const detailCountryVatPercentage = useMemo(() => {
    if (!detailDraft.countryCode) {
      return null;
    }

    const selectedCountry = countryData.find((country) => country.countryId === detailDraft.countryCode);
    const parsedVatPercentage = parseNumberOrNull(selectedCountry?.vatPercentage);
    return parsedVatPercentage === null ? null : parsedVatPercentage;
  }, [countryData, detailDraft.countryCode]);
  const selectedCurrency = useMemo(() => {
    if (!detailDraft.currencyId) {
      return null;
    }

    return (
      currencyData.find((currency) => String(currency.currencyId) === String(detailDraft.currencyId)) || null
    );
  }, [currencyData, detailDraft.currencyId]);
  const selectedCurrencyFractionDigits = useMemo(() => {
    const currencyIsoCode = selectedCurrency?.isoCode;
    if (!currencyIsoCode) {
      return 2;
    }

    try {
      return (
        new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currencyIsoCode,
        }).resolvedOptions().maximumFractionDigits ?? 2
      );
    } catch (error) {
      return 2;
    }
  }, [selectedCurrency?.isoCode]);
  const allowedVatDifference = useMemo(() => {
    const marginInSmallestUnit = parseNumberOrNull(selectedCurrency?.margin);
    if (marginInSmallestUnit === null) {
      return 0;
    }

    return marginInSmallestUnit / 10 ** selectedCurrencyFractionDigits;
  }, [selectedCurrency?.margin, selectedCurrencyFractionDigits]);
  const calculatedCountryVatAmount = useMemo(() => {
    const netAmount = parseNumberOrNull(detailDraft.invoiceGrossAmount);
    if (netAmount === null || detailCountryVatPercentage === null) {
      return null;
    }

    return (netAmount * detailCountryVatPercentage) / 100;
  }, [detailCountryVatPercentage, detailDraft.invoiceGrossAmount]);
  const isVatAmountMismatch = useMemo(() => {
    const enteredVatAmount = parseNumberOrNull(detailDraft.invoiceVATAmountLocalCurrency);
    if (enteredVatAmount === null || calculatedCountryVatAmount === null) {
      return false;
    }

    const vatDifference = Math.abs(enteredVatAmount - calculatedCountryVatAmount);
    return vatDifference - allowedVatDifference > 0.000001;
  }, [allowedVatDifference, calculatedCountryVatAmount, detailDraft.invoiceVATAmountLocalCurrency]);
  const currencyVatHelperText = useMemo(() => {
    if (!detailDraft.countryCode) {
      return "Select country first";
    }

    if (detailCountryVatPercentage === null) {
      return "No VAT % configured for this country";
    }

    const netAmount = parseNumberOrNull(detailDraft.invoiceGrossAmount);
    if (netAmount === null) {
      return `VAT %: ${detailCountryVatPercentage}`;
    }

    return `VAT %: ${detailCountryVatPercentage} | Calculated VAT: ${formatAmountToTwoDecimals(calculatedCountryVatAmount)}`;
  }, [calculatedCountryVatAmount, detailCountryVatPercentage, detailDraft.countryCode, detailDraft.invoiceGrossAmount]);
  const handleSelectDetailCustomer = useCallback(
    (customer) => {
      const nextDraft = {
        ...detailDraft,
        customerNumber: customer.customerNumber,
      };
      setDetailDraft(nextDraft);
      scheduleAutosave(nextDraft);
      setCustomerNumberError("");
    },
    [detailDraft, scheduleAutosave]
  );
  const handleClearDetailCustomer = useCallback(() => {
    const nextDraft = {
      ...detailDraft,
      customerNumber: "",
    };
    setDetailDraft(nextDraft);
    scheduleAutosave(nextDraft);
    setCustomerNumberError("");
  }, [detailDraft, scheduleAutosave]);
  const handleSelectUploadCustomer = useCallback((customer) => {
    setSelectedUploadCustomerNumber(customer.customerNumber);
    setUploadFiles((prevFiles) =>
      prevFiles.map((fileObj) =>
        fileObj.uploaded
          ? fileObj
          : {
              ...fileObj,
              customerNumber: customer.customerNumber,
              customerLabel: customer.label,
            }
      )
    );
  }, []);
  const selectedUploadCustomer = useMemo(() => {
    const customer = entityData.find((entity) => entity.entityNumber === selectedUploadCustomerNumber);
    if (!customer) {
      return null;
    }

    const customerNumber = String(customer.entityNumber || "").trim();
    const customerName = String(customer.name || "").trim();
    return {
      customerNumber,
      label: customerName ? `${customerNumber} - ${customerName}` : customerNumber,
    };
  }, [entityData, selectedUploadCustomerNumber]);

  const handleToggleHistoricUploadCustomer = useCallback((customerKey) => {
    setExpandedHistoricUploadCustomers((prevExpandedCustomers) => ({
      ...prevExpandedCustomers,
      [customerKey]: !prevExpandedCustomers[customerKey],
    }));
  }, []);

  const historicUploadColumns = useMemo(
    () => [
      {
        field: "customerNumber",
        headerName: "Customer",
        minWidth: 180,
        flex: 0.8,
        sortable: false,
        renderCell: (params) => {
          if (!params.row.isCustomerGroup) {
            return params.value || "";
          }

          const isExpanded = Boolean(expandedHistoricUploadCustomers[params.row.customerKey]);

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}>
              <IconButton
                size="small"
                aria-label={isExpanded ? "Collapse customer uploads" : "Expand customer uploads"}
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleHistoricUploadCustomer(params.row.customerKey);
                }}
              >
                {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
              {params.row.customerNumber}
            </Box>
          );
        },
      },
      {
        field: "manualInvoiceDraftId",
        headerName: "Draft ID",
        minWidth: 80,
        flex: 0.35,
        sortable: false,
        valueGetter: (params) => (params.row.isCustomerGroup ? "" : params.row.manualInvoiceDraftId),
      },
      {
        field: "originalFileName",
        headerName: "File name",
        minWidth: 180,
        flex: 1.4,
        sortable: false,
        valueGetter: (params) =>
          params.row.isCustomerGroup ? `${params.row.uploadCount} upload${params.row.uploadCount === 1 ? "" : "s"}` : params.row.originalFileName,
      },
      { field: "invoiceNumber", headerName: "Invoice number", minWidth: 170, flex: 0.75, sortable: false },
      { field: "status", headerName: "Status", minWidth: 90, flex: 0.45, sortable: false },
      {
        field: "createdDate",
        headerName: "Created",
        minWidth: 130,
        flex: 0.65,
        sortable: false,
        valueFormatter: (params) => (params.value ? moment(params.value).format("DD-MM-YYYY HH:mm") : ""),
      },
      {
        field: "lastUpdated",
        headerName: "Last updated",
        minWidth: 135,
        flex: 0.7,
        sortable: false,
        valueFormatter: (params) => (params.value ? moment(params.value).format("DD-MM-YYYY HH:mm") : ""),
      },
      {
        field: "uploadedByUserName",
        headerName: "Uploaded by",
        minWidth: 140,
        flex: 0.75,
        sortable: false,
        valueGetter: (params) => getUserDisplayName(params.row.uploadedByUserName, params.row.uploadedBy),
      },
      {
        field: "processedByDWSByUserName",
        headerName: "Processed by",
        minWidth: 140,
        flex: 0.75,
        sortable: false,
        valueGetter: (params) => getUserDisplayName(params.row.processedByDWSByUserName, params.row.processedByDWSBy),
      },
      {
        field: "processedByDWSDate",
        headerName: "Processing date",
        minWidth: 145,
        flex: 0.75,
        sortable: false,
        valueFormatter: (params) => (params.value ? moment(params.value).format("DD-MM-YYYY HH:mm") : ""),
      },
    ],
    [expandedHistoricUploadCustomers, handleToggleHistoricUploadCustomer]
  );
  const groupedHistoricUploadRows = useMemo(() => {
    const uploadsByCustomer = historicUploads.reduce((groups, upload) => {
      const customerNumber = String(upload.customerNumber || "").trim() || "Unknown customer";
      const customerKey = customerNumber;

      return {
        ...groups,
        [customerKey]: [...(groups[customerKey] || []), upload],
      };
    }, {});

    return Object.entries(uploadsByCustomer)
      .sort(([customerNumberA], [customerNumberB]) =>
        customerNumberA.localeCompare(customerNumberB, undefined, { numeric: true, sensitivity: "base" })
      )
      .flatMap(([customerKey, uploads]) => {
        const groupRow = {
          id: `customer-${customerKey}`,
          isCustomerGroup: true,
          customerKey,
          customerNumber: customerKey,
          uploadCount: uploads.length,
        };

        if (!expandedHistoricUploadCustomers[customerKey]) {
          return [groupRow];
        }

        return [
          groupRow,
          ...uploads.map((upload) => ({
            ...upload,
            id: `upload-${upload.manualInvoiceDraftId}`,
            customerKey,
          })),
        ];
      });
  }, [expandedHistoricUploadCustomers, historicUploads]);
  const filteredDraftRows = useMemo(() => {
    if (!selectedCustomerNumber) {
      return sortedDraftRows;
    }

    return sortedDraftRows.filter((draft) => String(draft.customerNumber || "").trim() === selectedCustomerNumber);
  }, [selectedCustomerNumber, sortedDraftRows]);
  const documentsToProcessCount = useMemo(
    () => filteredDraftRows.filter((draft) => !isDraftSelectable(draft)).length,
    [filteredDraftRows, isDraftSelectable]
  );
  const pdfViewerUrl = useMemo(() => getPdfViewerUrl(pdfUrl), [pdfUrl]);
  const getClipboardCountryLabel = useCallback(
    (countryCode) => {
      if (!countryCode) {
        return "";
      }

      return countryData.find((item) => Number(item.countryId) === Number(countryCode))?.countryCode || String(countryCode);
    },
    [countryData]
  );
  const getClipboardSupplierLabel = useCallback(
    (supplierId) => {
      if (!supplierId) {
        return "";
      }

      return mergedSupplierData.find((item) => Number(item.supplierId) === Number(supplierId))?.supplierName || String(supplierId);
    },
    [mergedSupplierData]
  );
  const getClipboardProductTypeLabel = useCallback(
    (countryCode, categoryCode) => {
      const normalizedCategoryCode = String(categoryCode || "").trim();
      if (!normalizedCategoryCode) {
        return "-";
      }

      const matchingProductType = productData.find(
        (item) =>
          Number(item.countryCode) === Number(countryCode) &&
          String(item.categoryCode) === normalizedCategoryCode
      );
      const productTypeDescription =
        matchingProductType?.productShortDescription || matchingProductType?.productLongDescription || normalizedCategoryCode;

      return `${normalizedCategoryCode} - ${productTypeDescription}`;
    },
    [productData]
  );
  const getClipboardProductSubCodeLabel = useCallback(
    (countryCode, categoryCode, productSubCode) => {
      const normalizedCategoryCode = String(categoryCode || "").trim();
      if (!normalizedCategoryCode) {
        return "-";
      }

      const normalizedSubCode = normalizeSubCode(productSubCode);
      if (normalizedSubCode === "98") {
        return "98. N/A";
      }

      const matchingSubCode = productData.find(
        (item) =>
          Number(item.countryCode) === Number(countryCode) &&
          String(item.categoryCode) === normalizedCategoryCode &&
          normalizeSubCode(item.subCode) === normalizedSubCode
      );
      const subCodeDescription =
        matchingSubCode?.productLongDescription || matchingSubCode?.productShortDescription || normalizedSubCode;

      return `${normalizedSubCode} - ${subCodeDescription}`;
    },
    [productData]
  );

  const applyClipboardSlotByNumber = useCallback(
    (slotNumber) => {
      if (!selectedDraftId) {
        setSnackbar({ open: true, severity: "warning", message: "Select a draft before pasting from clipboard." });
        return;
      }

      const slot = customerClipboardSlots.find((item) => item.slotNumber === slotNumber);
      if (!slot || !slot.hasValues) {
        setSnackbar({ open: true, severity: "warning", message: `Clipboard ${slotNumber} is empty.` });
        return;
      }

      const slotCountryCode = slot.countryCode || "";
      const normalizedSubCode = slot.categoryCode ? normalizeSubCode(slot.productSubCode) : "";
      const matchingProduct =
        slotCountryCode && slot.categoryCode
          ? productData.find(
              (item) =>
                Number(item.countryCode) === Number(slotCountryCode) &&
                String(item.categoryCode) === String(slot.categoryCode) &&
                normalizeSubCode(item.subCode) === normalizedSubCode
            )
          : null;
      const matchingCountry = countryData.find((item) => Number(item.countryId) === Number(slotCountryCode));
      const matchingSupplier = mergedSupplierData.find((item) => Number(item.supplierId) === Number(slot.supplierId));
      const isSupplierInCountry =
        !matchingSupplier || !slotCountryCode || Number(matchingSupplier.countryCode) === Number(slotCountryCode);

      const nextDraft = {
        ...detailDraft,
        countryCode: slotCountryCode,
        supplierId: isSupplierInCountry && slot.supplierId ? slot.supplierId : "",
        claimProductTypeCode: slot.categoryCode || "",
        claimProductSubCode: slot.categoryCode ? normalizedSubCode : "",
        claimProductTypeId: matchingProduct?.productTypeId || "",
        currencyId: matchingCountry?.currency || "",
      };

      setDetailDraft(nextDraft);
      scheduleAutosave(nextDraft);
      setSnackbar({ open: true, severity: "success", message: `Clipboard ${slotNumber} pasted.` });
    },
    [countryData, customerClipboardSlots, detailDraft, mergedSupplierData, productData, scheduleAutosave, selectedDraftId]
  );

  const saveClipboardSlotByNumber = useCallback(
    async (slotNumber) => {
      if (!selectedDraftId) {
        setSnackbar({ open: true, severity: "warning", message: "Select a draft before saving to clipboard." });
        return;
      }

      if (!selectedDetailEntityId) {
        setSnackbar({ open: true, severity: "warning", message: "A valid customer is required to save clipboards." });
        return;
      }

      try {
        const payload = {
          countryCode: detailDraft.countryCode || null,
          supplierId: detailDraft.supplierId || null,
          categoryCode: detailDraft.claimProductTypeCode || null,
          productSubCode: detailDraft.claimProductTypeCode
            ? normalizeSubCode(detailDraft.claimProductSubCode)
            : null,
        };
        const response = await axios.put(
          `/api/v1/entities/${selectedDetailEntityId}/manualInvoiceClipboardSlots/${slotNumber}`,
          payload
        );
        const normalizedUpdatedSlot = normalizeClipboardSlots([response.data]).find(
          (slot) => slot.slotNumber === slotNumber
        );
        if (!normalizedUpdatedSlot) {
          throw new Error("Invalid clipboard response.");
        }
        setCustomerClipboardSlots((prevSlots) =>
          prevSlots.map((slot) => (slot.slotNumber === slotNumber ? normalizedUpdatedSlot : slot))
        );
        setSnackbar({ open: true, severity: "success", message: `Clipboard ${slotNumber} saved.` });
      } catch (error) {
        setSnackbar({ open: true, severity: "error", message: `Failed to save clipboard ${slotNumber}.` });
      }
    },
    [detailDraft, normalizeClipboardSlots, selectedDetailEntityId, selectedDraftId]
  );
  const clearClipboardSlotByNumber = useCallback(
    async (slotNumber) => {
      if (!selectedDraftId) {
        setSnackbar({ open: true, severity: "warning", message: "Select a draft before clearing a clipboard." });
        return;
      }

      if (!selectedDetailEntityId) {
        setSnackbar({ open: true, severity: "warning", message: "A valid customer is required to clear clipboards." });
        return;
      }

      try {
        const response = await axios.put(
          `/api/v1/entities/${selectedDetailEntityId}/manualInvoiceClipboardSlots/${slotNumber}`,
          {
            countryCode: null,
            supplierId: null,
            categoryCode: null,
            productSubCode: null,
          }
        );
        const normalizedUpdatedSlot = normalizeClipboardSlots([response.data]).find(
          (slot) => slot.slotNumber === slotNumber
        );
        if (!normalizedUpdatedSlot) {
          throw new Error("Invalid clipboard response.");
        }

        setCustomerClipboardSlots((prevSlots) =>
          prevSlots.map((slot) => (slot.slotNumber === slotNumber ? normalizedUpdatedSlot : slot))
        );
        setSnackbar({ open: true, severity: "success", message: `Clipboard ${slotNumber} cleared.` });
      } catch (error) {
        setSnackbar({ open: true, severity: "error", message: `Failed to clear clipboard ${slotNumber}.` });
      }
    },
    [normalizeClipboardSlots, selectedDetailEntityId, selectedDraftId]
  );

  useEffect(() => {
    if (!selectedCustomerNumber) {
      return;
    }

    const selectedCustomerStillExists = customerFilterOptions.some(
      (option) => option.customerNumber === selectedCustomerNumber
    );
    if (!selectedCustomerStillExists) {
      setSelectedCustomerNumber("");
    }
  }, [customerFilterOptions, selectedCustomerNumber]);

  useEffect(() => {
    if (selectionEnabled) {
      return;
    }

    setSelectionModel((prevSelection) =>
      prevSelection.filter((id) => {
        const draft = filteredDraftRows.find((row) => row.manualInvoiceDraftId === id);
        return draft && isDraftSelectable(draft);
      })
    );
  }, [filteredDraftRows, isDraftSelectable, selectionEnabled]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const hasModifierConflict = event.altKey || event.metaKey;
      const shortcutSlotByKey = Number.parseInt(event.key, 10);
      const shortcutSlotByCode = Number.parseInt(String(event.code || "").replace(/^(Digit|Numpad)/, ""), 10);
      const shortcutSlot = Number.isInteger(shortcutSlotByKey)
        ? shortcutSlotByKey
        : Number.isInteger(shortcutSlotByCode)
          ? shortcutSlotByCode
          : null;
      const isClipboardShortcut =
        event.ctrlKey && !hasModifierConflict && shortcutSlot !== null && clipboardSlotNumbers.includes(shortcutSlot);

      if (isClipboardShortcut) {
        if (!selectedDraftId) {
          return;
        }

        event.preventDefault();
        if (event.shiftKey) {
          void saveClipboardSlotByNumber(shortcutSlot);
          return;
        }

        applyClipboardSlotByNumber(shortcutSlot);
        return;
      }

      if (!event.ctrlKey || event.key !== "Enter") {
        return;
      }

      event.preventDefault();

      const currentIndex = filteredDraftRows.findIndex(
        (draft) => draft.manualInvoiceDraftId === selectedDraftId
      );

      if (event.shiftKey) {
        const prevIndex = currentIndex <= 0 ? filteredDraftRows.length - 1 : currentIndex - 1;
        const prevDraft = filteredDraftRows[prevIndex];
        if (prevDraft) {
          handleSelectDraft(prevDraft);
        }
      } else {
        const nextIndex = currentIndex === -1 || currentIndex >= filteredDraftRows.length - 1 ? 0 : currentIndex + 1;
        const nextDraft = filteredDraftRows[nextIndex];
        if (nextDraft) {
          handleSelectDraft(nextDraft);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    applyClipboardSlotByNumber,
    filteredDraftRows,
    handleSelectDraft,
    saveClipboardSlotByNumber,
    selectedDraftId,
  ]);

  return (
    <React.Fragment>
      <Helmet title="Manual Invoices" />
      <Box sx={{ height: "calc(100vh - 230px)", minHeight: 0, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography variant="h3" gutterBottom>
          Manual Invoices
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {selectedDraftId && (
          <Box
            sx={{
              position: "fixed",
              top: isClipboardOverlayMinimized ? "calc(100vh - 56px)" : clipboardOverlayPosition.top,
              left: clipboardOverlayPosition.left,
              zIndex: 1401,
              width: isClipboardOverlayMinimized ? 320 : 760,
              maxHeight: isClipboardOverlayMinimized ? "none" : "60vh",
              overflowY: isClipboardOverlayMinimized ? "hidden" : "auto",
              border: "1px solid",
              borderColor: "primary.main",
              borderLeftWidth: 8,
              borderRadius: 1.5,
              backgroundColor: "background.paper",
              boxShadow: isClipboardOverlayMinimized ? 10 : 6,
              opacity: isClipboardOverlayMinimized ? 0.97 : 1,
              transition: "top 240ms ease, width 240ms ease, box-shadow 240ms ease, opacity 240ms ease",
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box
              onMouseDown={handleStartDraggingClipboardOverlay}
              sx={{
                cursor: isClipboardOverlayMinimized ? "default" : isClipboardOverlayDragging ? "grabbing" : "grab",
                userSelect: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: isClipboardOverlayMinimized ? 0 : 0.5,
                mx: -1.5,
                mt: -0.75,
                px: 1.5,
                py: 0.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Customer Clipboards
              </Typography>
              <IconButton
                size="small"
                aria-label={isClipboardOverlayMinimized ? "Maximize customer clipboards" : "Minimize customer clipboards"}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsClipboardOverlayMinimized((prevValue) => !prevValue);
                }}
              >
                {isClipboardOverlayMinimized ? <CropSquareIcon fontSize="small" /> : <HorizontalRuleIcon fontSize="small" />}
              </IconButton>
            </Box>
            {!isClipboardOverlayMinimized &&
              (loadingClipboardSlots ? (
                <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 0.25,
                  }}
                >
                  {customerClipboardSlots.map((slot) => (
                    <Paper
                      key={`clipboard-slot-${slot.slotNumber}`}
                      variant="outlined"
                      role="button"
                      tabIndex={slot.hasValues ? 0 : -1}
                      aria-label={`Paste clipboard ${slot.slotNumber}`}
                      onClick={() => {
                        if (!slot.hasValues) {
                          return;
                        }

                        applyClipboardSlotByNumber(slot.slotNumber);
                      }}
                      onKeyDown={(event) => {
                        if (!slot.hasValues) {
                          return;
                        }

                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          applyClipboardSlotByNumber(slot.slotNumber);
                        }
                      }}
                      sx={{
                        p: 0.35,
                        borderColor: slot.hasValues ? "primary.light" : "divider",
                        backgroundColor: slot.hasValues ? "rgba(25, 118, 210, 0.05)" : "background.paper",
                        cursor: slot.hasValues ? "pointer" : "default",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.15 }}
                        >
                          {slot.hasValues
                            ? `${slot.slotNumber}: ${getClipboardCountryLabel(slot.countryCode) || "-"}, ${getClipboardSupplierLabel(slot.supplierId) || "-"}, ${getClipboardProductTypeLabel(slot.countryCode, slot.categoryCode)}, ${getClipboardProductSubCodeLabel(slot.countryCode, slot.categoryCode, slot.productSubCode)}`
                            : `${slot.slotNumber}: Empty slot`}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 0, px: 0.5 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              void saveClipboardSlotByNumber(slot.slotNumber);
                            }}
                          >
                            <SaveOutlinedIcon fontSize="small" />
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 0, px: 0.5 }}
                            disabled={!slot.hasValues}
                            onClick={(event) => {
                              event.stopPropagation();
                              applyClipboardSlotByNumber(slot.slotNumber);
                            }}
                          >
                            <ContentPasteIcon fontSize="small" />
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={!slot.hasValues}
                            sx={{ minWidth: 0, px: 0.5 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              void clearClipboardSlotByNumber(slot.slotNumber);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ))}
          </Box>
        )}
        {selectedDraftId && matchedSupplierNotes.length > 0 && (
          <Box
            sx={{
              position: "fixed",
              top: noteOverlayPosition.top,
              left: noteOverlayPosition.left ?? "50%",
              transform: noteOverlayPosition.left === null ? "translateX(-50%)" : "none",
              zIndex: 1600,
              width: 350,
              minHeight: 150,
              maxHeight: "45vh",
              overflowY: "auto",
              border: "1px solid",
              borderColor: "#ed6c02",
              borderLeftWidth: 8,
              borderRadius: 1.5,
              backgroundColor: "#fff4e5",
              boxShadow: 6,
              px: 2,
              py: 1.5,
              color: "#663c00 !important",
              "& *": {
                color: "#663c00 !important",
              },
            }}
          >
            <Box
              onMouseDown={handleStartDraggingNoteOverlay}
              sx={{
                cursor: isNoteOverlayDragging ? "grabbing" : "grab",
                userSelect: "none",
                mb: 1,
                mx: -2,
                mt: -1.5,
                px: 2,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "#ed6c02",
                backgroundColor: "#fff4e5",
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#663c00 !important", fontWeight: 700 }}>
                Supplier Product Notes
              </Typography>
            </Box>
            {matchedSupplierNotes.map((note) => (
              <Typography
                key={note.supplierProductCombinationNoteId}
                variant="body2"
                sx={{ mb: 0.8, whiteSpace: "pre-wrap", color: "#1f1f1f !important" }}
              >
                {note.messageText}
              </Typography>
            ))}
          </Box>
        )}

        <Card sx={{ mb: 1 }}>
          <CardContent sx={{ py: "8px !important" }}>
            <Box
              sx={{
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Customer"
                  value={selectedCustomerNumber}
                  onChange={(event) => setSelectedCustomerNumber(event.target.value)}
                  disabled={customerFilterOptions.length === 0}
                  sx={{ minWidth: 280 }}
                >
                  <MenuItem value="">All customers</MenuItem>
                  {customerFilterOptions.map((option) => (
                    <MenuItem key={option.customerNumber} value={option.customerNumber}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, width: "100%" }}>
                        <Typography variant="body2" noWrap>
                          {option.label}
                        </Typography>
                        <Chip size="small" color="primary" variant="outlined" label={option.invoiceCount} />
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  Documents to process: {documentsToProcessCount}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {showHistoricUploadsButton && (
                  <Button variant="outlined" onClick={handleOpenHistoricUploadsDialog}>
                    Historic Uploads
                  </Button>
                )}
                <Button variant="contained" onClick={handleOpenBatchDialog}>
                  Upload Invoices
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

      <Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
        <PanelGroup direction="horizontal" onUpdate={(size) => setRightPaneSize(size)}>
          <Panel minSize={10} defaultSize={14}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", pb: "16px !important" }}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: 0 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                      rows={filteredDraftRows}
                      getRowId={(row) => row.manualInvoiceDraftId}
                      columns={columns}
                      checkboxSelection
                      disableSelectionOnClick
                      isRowSelectable={(params) => isGridRowSelectable(params.row)}
                      getRowClassName={(params) => {
                        const rowClasses = [];
                        if (params.row.manualInvoiceDraftId === selectedDraftId) {
                          rowClasses.push("manual-draft-detail-selected-row");
                        }

                        if (isDuplicateInvoiceForCustomer(params.row)) {
                          rowClasses.push("manual-draft-warning-row");
                        }

                        if (isDraftSelectable(params.row)) {
                          rowClasses.push("manual-draft-complete-row");
                        }

                        return rowClasses.join(" ");
                      }}
                      localeText={{
                        footerPaginationRowsPerPage: "Rijen:",
                        footerRowSelected: (count) => `${count} sel.`,
                      }}
                      selectionModel={selectionModel}
                      onSelectionModelChange={(newSelection) => {
                        if (selectionEnabled) {
                          setSelectionModel(newSelection);
                          return;
                        }

                        const selectableIds = filteredDraftRows
                          .filter((row) => isDraftSelectable(row))
                          .map((row) => row.manualInvoiceDraftId);
                        const filteredSelection = newSelection.filter((id) => selectableIds.includes(id));
                        setSelectionModel(filteredSelection);
                      }}
                      onRowClick={(params) => handleSelectDraft(params.row)}
                      components={{ Toolbar: GridToolbar }}
                      density="compact"
                      sx={{
                        height: "100%",
                        "& .MuiDataGrid-main": {
                          overflowX: "hidden",
                        },
                        "& .MuiDataGrid-virtualScroller": {
                          overflowX: "hidden",
                        },
                        "& .manual-draft-complete-row": {
                          backgroundColor: "rgba(76, 175, 80, 0.08)",
                        },
                        "& .manual-draft-complete-row:hover": {
                          backgroundColor: "rgba(76, 175, 80, 0.14)",
                        },
                        "& .manual-draft-warning-row": {
                          backgroundColor: "rgba(255, 193, 7, 0.12)",
                        },
                        "& .manual-draft-warning-row:hover": {
                          backgroundColor: "rgba(255, 193, 7, 0.2)",
                        },
                        "& .manual-draft-complete-row.manual-draft-warning-row": {
                          backgroundColor: "rgba(255, 193, 7, 0.12)",
                        },
                        "& .manual-draft-complete-row.manual-draft-warning-row:hover": {
                          backgroundColor: "rgba(255, 193, 7, 0.2)",
                        },
                        "& .manual-draft-detail-selected-row": {
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                        },
                        "& .manual-draft-detail-selected-row:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.12)",
                        },
                      }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 1,
                    pt: 1,
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "background.paper",
                    zIndex: 2,
                  }}
                >
                  <Button
                    onClick={() => setSelectionEnabled((prev) => !prev)}
                    sx={{
                      backgroundColor: selectionEnabled ? "error.light" : "inherit",
                      color: selectionEnabled ? "white" : "primary.main",
                      "&:hover": {
                        backgroundColor: selectionEnabled ? "error.main" : "inherit",
                      },
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      transition: "background-color 0.3s ease",
                    }}
                    variant="outlined"
                  >
                    {selectionEnabled ? "Disable Selection" : "Enable All Selection"}
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={handleDeleteSelectedDrafts}
                    disabled={publishing || deletingDraft || selectionModel.length === 0}
                  >
                    {deletingDraft ? "Deleting..." : "Delete selected"}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleOpenPublishDialog}
                    disabled={publishing || selectionModel.length === 0}
                  >
                    {publishing ? "Publishing..." : "Add selected to database"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Panel>
          <PanelResizeHandle style={{ width: "5px", background: "#ddd", cursor: "col-resize" }} />
          <Panel minSize={0} defaultSize={35} style={{ display: rightPaneSize === 0 ? "none" : "block" }}>
            <Card sx={{ height: "100%", position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 1305,
                  pointerEvents: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Tooltip title={splittingDraft ? "Splitting..." : "Split PDF to new draft"}>
                  <span>
                    <IconButton
                      onClick={handleOpenSplitDialog}
                      disabled={isSplitActionDisabled}
                      aria-label={splittingDraft ? "Splitting PDF" : "Split PDF"}
                      size="small"
                    >
                      <CallSplitIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={duplicatingDraft ? "Duplicating..." : "Duplicate invoice"}>
                  <span>
                    <IconButton
                      onClick={handleOpenDuplicateDialog}
                      disabled={isDuplicateActionDisabled}
                      aria-label={duplicatingDraft ? "Duplicating invoice" : "Duplicate invoice"}
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton onClick={() => setRightPaneSize(0)} tabIndex={-1} aria-label="Close details panel">
                  <CloseIcon />
                </IconButton>
              </Box>
              <CardContent sx={{ height: "100%", overflow: "hidden", pt: 5, display: "flex", flexDirection: "column" }}>
                {!selectedDraftId ? (
                  <Typography>Select a draft to edit invoice details.</Typography>
                ) : (
                  <React.Fragment>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        gap: 1,
                        height: "100%",
                        minHeight: 0,
                        alignItems: "stretch",
                      }}
                    >
                    <Box
                      ref={invoiceDetailPanelRef}
                      sx={{
                        flex: "0 0 auto",
                        width: { xs: "100%", lg: 250 },
                        minHeight: 0,
                        overflowY: "auto",
                        pt: 1.5,
                      }}
                    >
                      <Grid container rowSpacing={2.4} columnSpacing={0} sx={{ alignContent: "flex-start" }}>
                        <Grid item xs={12}>
                          <CustomerSearchSelect
                            customers={entityData}
                            value={detailDraft.customerNumber}
                            onSelect={handleSelectDetailCustomer}
                            onClear={handleClearDetailCustomer}
                            label="Customer Number"
                            helperText={customerNumberError || detailCustomerName || " "}
                            error={Boolean(customerNumberError)}
                            sx={responsiveFieldSx}
                            displayMode="number"
                            clearOnType
                            invoiceFieldKey="customerNumber"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            sx={responsiveFieldSx}
                            label="Invoice Number"
                            value={detailDraft.invoiceNumber}
                            inputProps={{ "data-invoice-field": "invoiceNumber" }}
                            onChange={(event) => {
                              handleDetailChange("invoiceNumber", event.target.value);
                              if (invoiceNumberInfo) {
                                setInvoiceNumberInfo("");
                              }
                            }}
                            onPaste={(event) => {
                              event.preventDefault();
                              const pasted = event.clipboardData.getData("text").trim();
                              handleDetailChange("invoiceNumber", pasted);
                              if (invoiceNumberInfo) {
                                setInvoiceNumberInfo("");
                              }
                            }}
                            onBlur={handleInvoiceNumberBlur}
                            helperText={invoiceNumberInfo}
                            FormHelperTextProps={{
                              sx: invoiceNumberInfo
                                ? {
                                    color: "warning.main",
                                  }
                                : undefined,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            sx={responsiveFieldSx}
                            label="Invoice Date"
                            type="date"
                            value={detailDraft.invoiceDate || ""}
                            onChange={(event) => handleDetailChange("invoiceDate", event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ "data-invoice-field": "invoiceDate" }}
                            inputRef={invoiceDateInputRef}
                            helperText={invoiceDateHelperText}
                            FormHelperTextProps={{
                              sx: hasInvoiceDateWarning
                                ? {
                                    color: "warning.main",
                                  }
                                : undefined,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            sx={responsiveFieldSx}
                            label="Country"
                            value={detailDraft.countryCode}
                            inputProps={{ "data-invoice-field": "countryCode" }}
                            helperText={detailCountryName || " "}
                            onChange={(event) => {
                              const selectedCountry = event.target.value;
                              const matchingCountry = countryData.find((item) => item.countryId === selectedCountry);
                              const nextDraft = {
                                ...detailDraft,
                                countryCode: selectedCountry,
                                supplierId: "",
                                claimProductTypeCode: "",
                                claimProductSubCode: "",
                                claimProductTypeId: "",
                                currencyId: matchingCountry?.currency || "",
                              };
                              setDetailDraft(nextDraft);
                              scheduleAutosave(nextDraft);
                            }}
                          >
                            {countryData.map((item) => (
                              <MenuItem key={item.countryId} value={item.countryId}>
                                {item.countryCode}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ position: "relative" }}>
                            <Tooltip title="Add supplier">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Add supplier"
                                  tabIndex={-1}
                                  onClick={() => setIsCreateSupplierDialogOpen(true)}
                                  disabled={!detailDraft.countryCode}
                                  disableRipple
                                  disableFocusRipple
                                  sx={{
                                    position: "absolute",
                                    top: -20,
                                    right: 16,
                                    zIndex: 2,
                                    p: 0.25,
                                    borderRadius: 0,
                                    bgcolor: "transparent",
                                    "&:hover": {
                                      bgcolor: "transparent",
                                    },
                                  }}
                                >
                                  <AddIcon fontSize="small" sx={{ color: "primary.main" }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <TextField
                              select
                              fullWidth
                              sx={responsiveFieldSx}
                              label="Supplier"
                              value={detailDraft.supplierId}
                              disabled={!detailDraft.countryCode}
                              inputProps={{ "data-invoice-field": "supplierId" }}
                              helperText={!detailDraft.countryCode ? "Select country first" : ""}
                              onChange={(event) => handleDetailChange("supplierId", event.target.value)}
                            >
                              {supplierOptions.map((item) => (
                                <MenuItem key={item.supplierId} value={item.supplierId}>
                                  {item.supplierName}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            sx={responsiveFieldSx}
                            label="Product Type"
                            value={detailDraft.claimProductTypeCode}
                            disabled={!detailDraft.countryCode}
                            inputProps={{ "data-invoice-field": "claimProductTypeCode" }}
                            helperText={!detailDraft.countryCode ? "Select country first" : ""}
                            onChange={(event) => {
                              const selectedTypeCode = event.target.value;
                              const subCodesForType = sortSubCodeOptions(
                                productData
                                  .filter(
                                    (item) =>
                                      item.countryCode === detailDraft.countryCode &&
                                      String(item.categoryCode) === String(selectedTypeCode)
                                  )
                                  .reduce((acc, item) => {
                                    const subCode = String(item.subCode ?? "");
                                    const normalizedSubCode = subCode === "" ? "98" : subCode;
                                    if (!acc.has(normalizedSubCode)) {
                                      acc.set(normalizedSubCode, {
                                        subCode: normalizedSubCode,
                                        label: item.productLongDescription || item.productShortDescription || "",
                                        productTypeId: item.productTypeId,
                                      });
                                    }
                                    return acc;
                                  }, new Map())
                                  .values()
                              );
                              const autoSubCode = subCodesForType.length === 1 ? subCodesForType[0] : null;
                              const nextDraft = {
                                ...detailDraft,
                                claimProductTypeCode: selectedTypeCode,
                                claimProductSubCode: autoSubCode ? autoSubCode.subCode : "",
                                claimProductTypeId: autoSubCode ? autoSubCode.productTypeId || "" : "",
                              };
                              setDetailDraft(nextDraft);
                              scheduleAutosave(nextDraft);
                            }}
                          >
                            {productTypeOptions.map((item) => (
                              <MenuItem key={item.code} value={item.code}>
                                {item.code} - {item.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            sx={responsiveFieldSx}
                            label="Product Sub Code"
                            value={detailDraft.claimProductSubCode}
                            disabled={!detailDraft.claimProductTypeCode}
                            inputProps={{ "data-invoice-field": "claimProductSubCode" }}
                            helperText={!detailDraft.claimProductTypeCode ? "Select product type first" : ""}
                            onChange={(event) => {
                              const selectedSubCode = event.target.value;
                              const matchedProduct = productSubCodeOptions.find((item) => item.subCode === selectedSubCode);
                              const nextDraft = {
                                ...detailDraft,
                                claimProductSubCode: selectedSubCode,
                                claimProductTypeId: matchedProduct?.productTypeId || "",
                              };
                              setDetailDraft(nextDraft);
                              scheduleAutosave(nextDraft);
                            }}
                          >
                            {productSubCodeOptions.map((item) => (
                              <MenuItem key={`${item.productTypeId}-${item.subCode}`} value={item.subCode}>
                                {item.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            sx={responsiveFieldSx}
                            label="Net Amount"
                            value={detailDraft.invoiceGrossAmount}
                            inputProps={{ "data-invoice-field": "invoiceGrossAmount" }}
                            onChange={(event) => handleDetailChange("invoiceGrossAmount", event.target.value)}
                            onPaste={(event) => {
                              event.preventDefault();
                              const pasted = event.clipboardData.getData("text").trim();
                              handleDetailChange("invoiceGrossAmount", pasted);
                            }}
                            onBlur={() =>
                              handleDetailChange(
                                "invoiceGrossAmount",
                                formatAmountToTwoDecimals(detailDraft.invoiceGrossAmount)
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            sx={responsiveFieldSx}
                            label="VAT Amount"
                            value={detailDraft.invoiceVATAmountLocalCurrency}
                            inputProps={{ "data-invoice-field": "invoiceVATAmountLocalCurrency" }}
                            onChange={(event) => handleDetailChange("invoiceVATAmountLocalCurrency", event.target.value)}
                            onPaste={(event) => {
                              event.preventDefault();
                              const pasted = event.clipboardData.getData("text").trim();
                              handleDetailChange("invoiceVATAmountLocalCurrency", pasted);
                            }}
                            onBlur={() =>
                              handleDetailChange(
                                "invoiceVATAmountLocalCurrency",
                                formatAmountToTwoDecimals(detailDraft.invoiceVATAmountLocalCurrency)
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            sx={responsiveFieldSx}
                            label="Currency"
                            value={detailDraft.currencyId}
                            inputProps={{ "data-invoice-field": "currencyId" }}
                            onChange={(event) => handleDetailChange("currencyId", event.target.value)}
                            helperText={currencyVatHelperText}
                            FormHelperTextProps={{
                              sx: isVatAmountMismatch
                                ? {
                                    color: "error.main",
                                  }
                                : undefined,
                            }}
                          >
                            {currencyData.map((item) => (
                              <MenuItem key={item.currencyId} value={item.currencyId}>
                                {item.isoCode}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", position: "relative", zIndex: 1210 }}>
                      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        PDF Preview
                      </Typography>
                      {!pdfUrl ? (
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography>No PDF available.</Typography>
                        </Box>
                      ) : (
                        <object
                          data={pdfViewerUrl}
                          type="application/pdf"
                          width="100%"
                          style={{ flex: 1, minHeight: 0, border: "1px solid #e0e0e0", borderRadius: 4 }}
                        >
                          <iframe title="Manual draft PDF" src={pdfViewerUrl} width="100%" height="100%" />
                        </object>
                      )}
                      <Box
                        sx={{
                          mt: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Previous files
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setIsOldFilesDialogOpen(true)}
                          disabled={loadingDraftFileHistory || oldFilesList.length === 0}
                        >
                          Open Old Files
                        </Button>
                        {loadingDraftFileHistory && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.8 }}>
                            <CircularProgress size={14} />
                            <Typography variant="caption">Loading previous files...</Typography>
                          </Box>
                        )}
                        {!loadingDraftFileHistory && oldFilesList.length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.8 }}>
                            No previous files recorded.
                          </Typography>
                        )}
                      </Box>
                      </Box>
                    </Box>
                    </Box>
                  </React.Fragment>
                )}
              </CardContent>
            </Card>
          </Panel>
        </PanelGroup>
      </Box>
      </Box>
      {isCreateSupplierDialogOpen && (
        <Box
          role="button"
          tabIndex={0}
          aria-label="Close add supplier popup overlay"
          onClick={handleCloseCreateSupplierDialog}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " " || event.key === "Escape") {
              event.preventDefault();
              handleCloseCreateSupplierDialog();
            }
          }}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            backgroundColor: "rgba(0, 0, 0, 0.12)",
            cursor: "pointer",
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={isPublishDialogOpen} onClose={handleClosePublishDialog} fullWidth maxWidth="md">
        <DialogTitle>Add Selected to Database</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Review the selected manual invoice drafts and click Upload to add them to the database.
          </DialogContentText>
          <Paper sx={{ height: 420, mt: 2, mb: 2, overflow: "hidden", position: "relative" }}>
            <Box sx={{ height: "calc(100% - 50px)", overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Customer", "File Name", "Invoice Number", "Upload Progress", "Actions"].map((header) => (
                      <th
                        key={header}
                        style={{
                          height: 30,
                          borderBottom: "1px solid #ccc",
                          padding: "4px 8px",
                          textAlign: "left",
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {publishRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 12, color: "#666" }}>
                        No draft invoices selected.
                      </td>
                    </tr>
                  ) : (
                    publishRows.map((row) => (
                      <tr key={row.id}>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          {row.customerNumber}
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          {row.fileName}
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          {row.invoiceNumber}
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          <LinearProgress variant="determinate" value={row.progress} />
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                            {row.published ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : row.error ? (
                              <Tooltip title={row.error}>
                                <ErrorIcon color="error" fontSize="small" />
                              </Tooltip>
                            ) : row.publishing ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Typography variant="body2">Ready</Typography>
                            )}
                          </Box>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Box>
            <Box
              sx={{
                height: 50,
                display: "flex",
                alignItems: "center",
                px: 2,
                borderTop: "1px solid #ccc",
                position: "absolute",
                bottom: 0,
                width: "100%",
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="body2">
                Total drafts to be uploaded: {publishRows.filter((row) => !row.error && !row.published).length}
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handlePublishSelected}
            disabled={publishing || publishRows.filter((row) => !row.error && !row.published).length === 0}
          >
            {publishing ? "Uploading..." : "Upload"}
          </Button>
          <Button onClick={handleClosePublishDialog} disabled={publishing}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isHistoricUploadsDialogOpen}
        onClose={() => setIsHistoricUploadsDialogOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Historic Uploads</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            View processed manual invoice uploads.
          </DialogContentText>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
            <DatePicker
              label="Created from"
              inputFormat="dd-MM-yyyy"
              mask="__-__-____"
              value={historicUploadFilters.createdDateFrom}
              onChange={(value, keyboardInputValue) =>
                handleHistoricUploadFilterChange("createdDateFrom", value, keyboardInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="dd-mm-yyyy"
                  error={getHistoricUploadDateFieldError("createdDateFrom", params.inputProps?.value)}
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  sx={{ minWidth: 170 }}
                />
              )}
            />
            <DatePicker
              label="Created to"
              inputFormat="dd-MM-yyyy"
              mask="__-__-____"
              value={historicUploadFilters.createdDateTo}
              onChange={(value, keyboardInputValue) =>
                handleHistoricUploadFilterChange("createdDateTo", value, keyboardInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="dd-mm-yyyy"
                  error={getHistoricUploadDateFieldError("createdDateTo", params.inputProps?.value)}
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  sx={{ minWidth: 170 }}
                />
              )}
            />
            <DatePicker
              label="Last updated from"
              inputFormat="dd-MM-yyyy"
              mask="__-__-____"
              value={historicUploadFilters.lastUpdatedFrom}
              onChange={(value, keyboardInputValue) =>
                handleHistoricUploadFilterChange("lastUpdatedFrom", value, keyboardInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="dd-mm-yyyy"
                  error={getHistoricUploadDateFieldError("lastUpdatedFrom", params.inputProps?.value)}
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  sx={{ minWidth: 190 }}
                />
              )}
            />
            <DatePicker
              label="Last updated to"
              inputFormat="dd-MM-yyyy"
              mask="__-__-____"
              value={historicUploadFilters.lastUpdatedTo}
              onChange={(value, keyboardInputValue) =>
                handleHistoricUploadFilterChange("lastUpdatedTo", value, keyboardInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="dd-mm-yyyy"
                  error={getHistoricUploadDateFieldError("lastUpdatedTo", params.inputProps?.value)}
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  sx={{ minWidth: 190 }}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={handleApplyHistoricUploadFilters}
              disabled={!showHistoricUploadRows || loadingHistoricUploads}
            >
              Filter
            </Button>
            <Button onClick={handleResetHistoricUploadFilters} disabled={!showHistoricUploadRows || loadingHistoricUploads}>
              Reset
            </Button>
          </Box>
          <Paper sx={{ height: 520, overflow: "hidden" }}>
            <DataGrid
              rows={showHistoricUploadRows ? groupedHistoricUploadRows : []}
              getRowId={(row) => row.id}
              columns={historicUploadColumns}
              loading={showHistoricUploadRows && loadingHistoricUploads}
              components={{ Toolbar: GridToolbar }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    manualInvoiceDraftId: false,
                  },
                },
              }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              density="compact"
              onRowClick={(params) => {
                if (params.row.isCustomerGroup) {
                  handleToggleHistoricUploadCustomer(params.row.customerKey);
                }
              }}
              getRowClassName={(params) => (params.row.isCustomerGroup ? "historic-upload-customer-group-row" : "")}
              localeText={{
                noRowsLabel: "No historic uploads found.",
                footerPaginationRowsPerPage: "Rows:",
              }}
              sx={{
                "& .MuiDataGrid-main": {
                  overflowX: "hidden",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflowX: "hidden",
                },
                "& .historic-upload-customer-group-row": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "& .historic-upload-customer-group-row:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                },
              }}
            />
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => loadHistoricUploads()} disabled={!showHistoricUploadRows || loadingHistoricUploads}>
            Refresh
          </Button>
          <Button onClick={() => setIsHistoricUploadsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isBatchDialogOpen} onClose={handleCloseBatchDialog} fullWidth maxWidth="lg">
        <DialogTitle>Upload Invoices</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Search and select a customer before uploading manual invoice PDFs.
          </DialogContentText>
          <CustomerSearchSelect
            customers={entityData}
            value={selectedUploadCustomerNumber}
            onSelect={handleSelectUploadCustomer}
            onClear={() => setSelectedUploadCustomerNumber("")}
            size="small"
            disabled={uploading}
            sx={{ mb: 2 }}
            clearOnType
          />
          <Typography variant="body2" sx={{ mb: 1, color: selectedUploadCustomer ? "text.primary" : "warning.main" }}>
            {selectedUploadCustomer ? `Selected customer: ${selectedUploadCustomer.label}` : "Select a customer to enable upload."}
          </Typography>
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "primary.main" : "divider",
              borderRadius: 1,
              minHeight: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              textAlign: "center",
              cursor: uploading || !selectedUploadCustomerNumber ? "default" : "pointer",
              backgroundColor: isDragActive ? "action.hover" : "transparent",
              opacity: selectedUploadCustomerNumber ? 1 : 0.55,
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="body1">
              {uploading
                ? "Uploading PDFs..."
                : selectedUploadCustomerNumber
                ? "Drag and drop PDF invoices here, or click to select files"
                : "Select a customer first"}
            </Typography>
          </Box>
          <Paper sx={{ height: 420, mt: 2, mb: 2, overflow: "hidden", position: "relative" }}>
            <Box sx={{ height: "calc(100% - 50px)", overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Customer", "File Name", "File Size", "MIME Type", "Upload Progress", "Actions"].map((header) => (
                      <th
                        key={header}
                        style={{
                          height: 30,
                          borderBottom: "1px solid #ccc",
                          padding: "4px 8px",
                          textAlign: "left",
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uploadFiles.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 12, color: "#666" }}>
                        No invoices added yet.
                      </td>
                    </tr>
                  ) : (
                    uploadFiles.map((fileObj) => (
                      <tr key={fileObj.id}>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          {fileObj.customerLabel || fileObj.customerNumber || ""}
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          {fileObj.file.name}
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          {(fileObj.size / 1024).toFixed(2)} KB
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          {fileObj.type}
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          <LinearProgress variant="determinate" value={fileObj.progress} />
                        </td>
                        <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                            {fileObj.uploaded ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : fileObj.error ? (
                              <React.Fragment>
                                <Tooltip title={fileObj.error}>
                                  <ErrorIcon color="error" fontSize="small" />
                                </Tooltip>
                                {!uploading && (
                                  <IconButton
                                    size="small"
                                    aria-label="Remove invoice"
                                    onClick={() => handleRemoveUploadFile(fileObj.id)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </React.Fragment>
                            ) : (
                              !fileObj.uploading && (
                                <IconButton
                                  size="small"
                                  aria-label="Remove invoice"
                                  onClick={() => handleRemoveUploadFile(fileObj.id)}
                                  disabled={uploading}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )
                            )}
                          </Box>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Box>
            <Box
              sx={{
                height: 50,
                display: "flex",
                alignItems: "center",
                px: 2,
                borderTop: "1px solid #ccc",
                position: "absolute",
                bottom: 0,
                width: "100%",
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="body2">
                Total invoices to be uploaded: {uploadFiles.filter((fileObj) => !fileObj.error && !fileObj.uploaded).length}
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleUploadManualFiles}
            disabled={
              uploading ||
              !selectedUploadCustomerNumber ||
              uploadFiles.filter((fileObj) => !fileObj.error && !fileObj.uploaded).length === 0
            }
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
          <Button onClick={handleCloseBatchDialog} disabled={uploading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isDuplicateDialogOpen} onClose={() => (duplicatingDraft ? null : setIsDuplicateDialogOpen(false))}>
        <DialogTitle>Duplicate draft</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Duplicate this invoice for customer {String(detailDraft.customerNumber || "").trim()} with invoice number "
            {duplicateInvoiceNumber}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDuplicateDialogOpen(false)} disabled={duplicatingDraft}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmDuplicateDraft} disabled={duplicatingDraft}>
            {duplicatingDraft ? "Duplicating..." : "Duplicate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isSplitDialogOpen}
        onClose={handleCloseSplitDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { minHeight: "82vh", height: "82vh", display: "flex", flexDirection: "column" } }}
      >
        <DialogTitle>Split PDF to new draft</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <DialogContentText sx={{ mb: 1.5 }}>
            Select the pages you want to save as a separate draft for customer {String(detailDraft.customerNumber || "").trim()}.
          </DialogContentText>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, flex: 1, minHeight: 0 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "minmax(320px, 36%) 1fr", gap: 2, height: "100%", minHeight: 0 }}>
              <Box sx={{ borderRight: "1px solid", borderColor: "divider", pr: 2, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>
                  <FormGroup>
                    {Array.from({ length: splitPageCount }, (_, index) => {
                      const pageNumber = index + 1;
                      const isActivePreview = activeSplitPreviewPage === pageNumber;
                      return (
                        <Box
                          key={pageNumber}
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveSplitPreviewPage(pageNumber)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setActiveSplitPreviewPage(pageNumber);
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 0.8,
                            mb: 0.6,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: isActivePreview ? "primary.main" : "divider",
                            bgcolor: isActivePreview ? "action.selected" : "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <Checkbox
                            checked={selectedSplitPages.includes(pageNumber)}
                            onChange={() => handleToggleSplitPage(pageNumber)}
                            onClick={(event) => event.stopPropagation()}
                          />
                          <Box
                            sx={{
                              width: 56,
                              height: 74,
                              borderRadius: 0.5,
                              border: "1px solid",
                              borderColor: "divider",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "grey.100",
                              flexShrink: 0,
                            }}
                          >
                            {splitPagePreviews[pageNumber] ? (
                              <Box
                                component="object"
                                data={splitPagePreviews[pageNumber]}
                                type="application/pdf"
                                sx={{ width: "100%", height: "100%", display: "block", backgroundColor: "common.white" }}
                              >
                                <Typography variant="caption" sx={{ fontSize: "0.58rem" }}>
                                  No preview
                                </Typography>
                              </Box>
                            ) : loadingSplitPagePreviews ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Typography variant="caption" sx={{ fontSize: "0.58rem" }}>
                                No preview
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="body2">{`Page ${pageNumber}`}</Typography>
                        </Box>
                      );
                    })}
                  </FormGroup>
                </Box>
              </Box>
              <Box sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Preview page {activeSplitPreviewPage}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "grey.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {splitPagePreviews[activeSplitPreviewPage] ? (
                    <Box
                      component="object"
                      data={splitPagePreviews[activeSplitPreviewPage]}
                      type="application/pdf"
                      sx={{ width: "100%", height: "100%", display: "block", backgroundColor: "common.white" }}
                    >
                      <Typography variant="body2">Preview unavailable</Typography>
                    </Box>
                  ) : loadingSplitPagePreviews ? (
                    <CircularProgress />
                  ) : (
                    <Typography variant="body2">Preview unavailable</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={removeSelectedPagesFromCurrentDraft}
                onChange={(event) => setRemoveSelectedPagesFromCurrentDraft(event.target.checked)}
              />
            }
            label="Also remove selected pages from current draft PDF"
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleCloseSplitDialog} disabled={splittingDraft}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmSplitDraft}
              disabled={splittingDraft || selectedSplitPages.length === 0}
            >
              {splittingDraft ? "Saving..." : "Save as new draft"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={draftIdsToDelete.length > 0} onClose={handleCloseDeleteDraftDialog}>
        <DialogTitle>
          {draftIdsToDelete.length === 1 ? "Delete draft" : `Delete ${draftIdsToDelete.length} drafts`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {draftIdsToDelete.length === 1
              ? "Are you sure you want to delete this draft invoice? It will be hidden from Manual Invoices."
              : "Are you sure you want to delete the selected draft invoices? They will be hidden from Manual Invoices."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDraftDialog} disabled={deletingDraft}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDeleteDraft} disabled={deletingDraft}>
            {deletingDraft ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOldFilesDialogOpen}
        onClose={() => {
          if (restoringOldFileId) {
            return;
          }

          setIsOldFilesDialogOpen(false);
          setActiveOldFilePreviewId(null);
        }}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Old files</DialogTitle>
        <DialogContent>
          {loadingDraftFileHistory ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading old files...</Typography>
            </Box>
          ) : oldFilesList.length === 0 ? (
            <Typography variant="body2">No old files recorded for this draft.</Typography>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "minmax(340px, 38%) 1fr", gap: 2, minHeight: "56vh" }}>
              <Box sx={{ maxHeight: "56vh", overflowY: "auto", pr: 1 }}>
                {oldFilesList.map((historyItem) => {
                  const isRestoring = restoringOldFileId === historyItem.previousFileId;
                  const isActive = activeOldFilePreviewId === historyItem.previousFileId;
                  return (
                    <Box
                      key={historyItem.previousFileId}
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveOldFilePreviewId(historyItem.previousFileId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setActiveOldFilePreviewId(historyItem.previousFileId);
                        }
                      }}
                      sx={{
                        border: "1px solid",
                        borderColor: isActive ? "primary.main" : "divider",
                        borderRadius: 1,
                        p: 1.2,
                        mb: 1,
                        cursor: "pointer",
                        bgcolor: isActive ? "action.selected" : "transparent",
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 0.8 }}>
                        File ID {historyItem.previousFileId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        {moment(historyItem.performedDate).format("DD-MM-YYYY HH:mm")} by{" "}
                        {historyItem.performedByUserName || historyItem.performedBy}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDraftHistoryFile(historyItem.previousFileId);
                          }}
                          disabled={Boolean(restoringOldFileId)}
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRestoreOldFile(historyItem.previousFileId);
                          }}
                          disabled={Boolean(restoringOldFileId)}
                        >
                          {isRestoring ? "Restoring..." : "Restore"}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, minHeight: 0, overflow: "hidden" }}>
                <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2">
                    {activeOldFilePreviewId ? `Preview file ${activeOldFilePreviewId}` : "Preview"}
                  </Typography>
                </Box>
                <Box sx={{ height: "calc(56vh - 44px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {loadingOldFilePreview ? (
                    <CircularProgress />
                  ) : oldFilePreviewUrl ? (
                    <object data={oldFilePreviewUrl} type="application/pdf" width="100%" height="100%">
                      <Typography variant="body2">Preview unavailable</Typography>
                    </object>
                  ) : (
                    <Typography variant="body2">Preview unavailable</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsOldFilesDialogOpen(false);
              setActiveOldFilePreviewId(null);
            }}
            disabled={Boolean(restoringOldFileId)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <CreateSupplierDialog
        open={isCreateSupplierDialogOpen}
        onClose={handleCloseCreateSupplierDialog}
        onCreated={handleSupplierCreated}
        defaultCountryCode={detailDraft.countryCode}
        suppliers={mergedSupplierData}
      />
    </React.Fragment>
  );
};

export default ManualInvoices;
